import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Text, Card, DataTable, Icon, Box, Modal, TextField, Button, Popover, BlockStack, Select, Page, Spinner, Banner, Checkbox, InlineStack, Tooltip, ActionList
} from '@shopify/polaris';
import {
  EditIcon, CashDollarIcon, ClockIcon, PersonFilledIcon, PlusCircleIcon, DisabledIcon, DeleteIcon, CalendarIcon, ExternalIcon, ColorIcon, PrintIcon, DuplicateIcon, XSmallIcon
} from '@shopify/polaris-icons';
import { DatePicker } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAxiosClient from '../axiosClient';
import { formatTime, convert24To12, convert12To24 } from '../../utils/timeFormat';
import '../../i18n';

const getUserTimezone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

// Beautiful color palette for locations - Black added as first option
const getLocationColors = (t) => [
  { name: t('black'), value: '#000000', text: '#FFFFFF' },
  { name: t('ocean_blue'), value: '#0EA5E9', text: '#FFFFFF' },
  { name: t('emerald_green'), value: '#10B981', text: '#FFFFFF' },
  { name: t('purple_violet'), value: '#8B5CF6', text: '#FFFFFF' },
  { name: t('rose_pink'), value: '#F43F5E', text: '#FFFFFF' },
  { name: t('amber_orange'), value: '#F59E0B', text: '#000000' },
  { name: t('indigo_blue'), value: '#6366F1', text: '#FFFFFF' },
  { name: t('teal_green'), value: '#14B8A6', text: '#FFFFFF' },
  { name: t('red_crimson'), value: '#EF4444', text: '#FFFFFF' },
  { name: t('slate_gray'), value: '#64748B', text: '#FFFFFF' },
  { name: t('lime_green'), value: '#84CC16', text: '#000000' },
  { name: t('pink_fuchsia'), value: '#EC4899', text: '#FFFFFF' },
  { name: t('yellow_sun'), value: '#EAB308', text: '#000000' },
  { name: t('cyan_blue'), value: '#06B6D4', text: '#FFFFFF' },
  { name: t('green_forest'), value: '#059669', text: '#FFFFFF' },
  { name: t('orange_sunset'), value: '#EA580C', text: '#FFFFFF' },
];

// Default black color as the default selection
const getDefaultLocationColor = (t) => ({ name: t('black'), value: '#000000', text: '#FFFFFF' });

// LocalStorage utilities for location colors
const LOCATION_COLORS_STORAGE_KEY = 'schedule_location_colors';

const getLocationColorsFromStorage = () => {
  try {
    const stored = localStorage.getItem(LOCATION_COLORS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
};

const saveLocationColorToStorage = (locationId, color) => {
  try {
    const storedColors = getLocationColorsFromStorage();
    storedColors[locationId.toString()] = color;
    localStorage.setItem(LOCATION_COLORS_STORAGE_KEY, JSON.stringify(storedColors));
  } catch (error) {
    // Error handled silently
  }
};

const removeLocationColorFromStorage = (locationId) => {
  try {
    const storedColors = getLocationColorsFromStorage();
    delete storedColors[locationId.toString()];
    localStorage.setItem(LOCATION_COLORS_STORAGE_KEY, JSON.stringify(storedColors));
  } catch (error) {
    // Error handled silently
  }
};

// Auto-assign colors to new locations - Black is first priority
const assignColorToLocation = (locationId, existingColors, t) => {
  const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);
  const LOCATION_COLORS = getLocationColors(t);

  // Always assign black as the default for new locations
  if (!existingColors[locationId.toString()]) {
    saveLocationColorToStorage(locationId, DEFAULT_LOCATION_COLOR.value);
    return DEFAULT_LOCATION_COLOR.value;
  }

  // If for some reason we need to assign a different color, use the existing logic
  const usedColors = Object.values(existingColors);
  const availableColors = LOCATION_COLORS.filter(color => !usedColors.includes(color.value));

  if (availableColors.length > 0) {
    // Assign first available color (black will be first if available)
    const assignedColor = availableColors[0];
    saveLocationColorToStorage(locationId, assignedColor.value);
    return assignedColor.value;
  } else {
    // If all colors are used, cycle through them starting with black
    const colorIndex = Object.keys(existingColors).length % LOCATION_COLORS.length;
    const assignedColor = LOCATION_COLORS[colorIndex];
    saveLocationColorToStorage(locationId, assignedColor.value);
    return assignedColor.value;
  }
};

// LocalStorage utilities for staff colors
const STAFF_COLORS_STORAGE_KEY = 'schedule_staff_colors';

const getStaffColorsFromStorage = () => {
  try {
    const stored = localStorage.getItem(STAFF_COLORS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    return {};
  }
};

const saveStaffColorToStorage = (staffId, color) => {
  try {
    const storedColors = getStaffColorsFromStorage();
    storedColors[staffId.toString()] = color;
    localStorage.setItem(STAFF_COLORS_STORAGE_KEY, JSON.stringify(storedColors));
  } catch (error) {
    // Error handled silently
  }
};

const removeStaffColorFromStorage = (staffId) => {
  try {
    const storedColors = getStaffColorsFromStorage();
    delete storedColors[staffId.toString()];
    localStorage.setItem(STAFF_COLORS_STORAGE_KEY, JSON.stringify(storedColors));
  } catch (error) {
    // Error handled silently
  }
};

// Auto-assign colors to new staff - Black is first priority
const assignColorToStaff = (staffId, existingColors, t) => {
  const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);
  const LOCATION_COLORS = getLocationColors(t);

  // Always assign black as the default for new staff
  if (!existingColors[staffId.toString()]) {
    saveStaffColorToStorage(staffId, DEFAULT_LOCATION_COLOR.value);
    return DEFAULT_LOCATION_COLOR.value;
  }

  const usedColors = Object.values(existingColors);
  const availableColors = LOCATION_COLORS.filter(color => !usedColors.includes(color.value));

  if (availableColors.length > 0) {
    const assignedColor = availableColors[0];
    saveStaffColorToStorage(staffId, assignedColor.value);
    return assignedColor.value;
  } else {
    const colorIndex = Object.keys(existingColors).length % LOCATION_COLORS.length;
    const assignedColor = LOCATION_COLORS[colorIndex];
    saveStaffColorToStorage(staffId, assignedColor.value);
    return assignedColor.value;
  }
};

// Update the formatDateForBackend function
const formatDateForBackend = (date) => {
  let dateObj;
  if (typeof date === 'string') {
    const [year, month, day] = date.split('-');
    dateObj = new Date(year, month - 1, day);
  } else {
    dateObj = new Date(date);
  }

  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Enhanced TimePicker Component Styles with Beautiful Time-off indicators
const timePickerStyles = `
.zoneStaff_timePicker_container {
    position: relative;
    display: inline-block;
    width: 100%;
}
.zoneStaff_timePicker_inputContainer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    cursor: pointer;
    border-radius: 8px;
    border: 1px solid #8e8e8e;
    background: #ffffff;
    transition: all 0.2s ease;
}
.zoneStaff_timePicker_inputContainer:hover {
    border-color: black;
}
.zoneStaff_timePicker_input {
    border: none;
    outline: none;
    width: 100%;
    font-size: 14px;
    color: #212b36;
}
.zoneStaff_timePicker_dropdown {
    position: absolute;
    bottom: calc(100% - 8px);
    left: 0;
    width: 80%;
    display: flex;
    justify-content: center;
    background: #ffffff;
    border: 1px solid #8e8e8e;
    border-radius: 8px;
    z-index: 1000;
    overflow: hidden;
    box-shadow: 0 2px 16px rgba(33, 43, 54, 0.08);
    box-sizing: border-box;
}
.zoneStaff_timePicker_highlighted_row {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: calc(100% - 12px);
    height: 32px;
    background-color: #f4f6f8;
    border-radius: 4px;
    pointer-events: none;
    z-index: 1;
    margin: 0 6px;
}
.zoneStaff_timePicker_column {
    position: relative;
    overflow-y: auto;
    text-align: center;
    padding: 0;
    width: 20%;
    height: 160px;
    -ms-overflow-style: none;
    scrollbar-width: none;
    z-index: 2;
}
.zoneStaff_timePicker_column::-webkit-scrollbar {
    display: none;
}
.zoneStaff_timePicker_item {
    height: 32px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    scroll-snap-align: center;
    transition: all 0.2s ease;
    font-size: 14px;
    color: #637381;
}
.zoneStaff_timePicker_item:hover {
    background-color: #f4f6f8;
    color: #212b36;
}
.zoneStaff_timePicker_item.selected {
    color: black;
    font-weight: 500;
}
.zoneStaff_timePicker_colon {
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5em;
    padding: 0 5px;
    z-index: 2;
}
.zoneStaff_timePicker_icon {
    height: 18px;
    width: 18px;
}

/* Beautiful Time-off Indicator Styles */
.timeoff-requested {
    background: linear-gradient(135deg, #FEF3C7 0%, #F59E0B 100%);
    border: 1px solid #D97706;
    color: #92400E;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeoff-requested:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(217, 119, 6, 0.3);
    background: linear-gradient(135deg, #FDE68A 0%, #F59E0B 100%);
}

.timeoff-requested::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #F59E0B, transparent);
    animation: shimmer 2.5s infinite;
}

.timeoff-approved {
    background: linear-gradient(135deg, #D1FAE5 0%, #10B981 100%);
    border: 1px solid #059669;
    color: #065F46;
    position: relative;
    overflow: hidden;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeoff-approved:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 0 8px 25px rgba(5, 150, 105, 0.3);
    background: linear-gradient(135deg, #A7F3D0 0%, #10B981 100%);
}

.timeoff-approved::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #10B981, transparent);
    animation: shimmer 2.5s infinite;
}

.timeoff-rejected {
    background: linear-gradient(135deg, #FEE2E2 0%, #EF4444 100%);
    border: 1px solid #DC2626;
    color: #991B1B;
    cursor: not-allowed;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.timeoff-rejected:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.timeoff-indicator {
    border-radius: 8px;
    padding: 4px 8px;
    margin: 2px 0;
    font-size: 9px;
    font-weight: 700;
    text-align: center;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(10px);
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    min-height: 20px;
}

.timeoff-indicator .external-icon {
    width: 10px;
    height: 10px;
    opacity: 0.8;
}

.timeoff-indicator-content {
    display: flex;
    align-items: center;
    gap: 2px;
    width: 100%;
    justify-content: space-between;
}

.timeoff-status-text {
    flex: 1;
    text-align: left;
}

/* Color picker styles */
.color-palette {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    padding: 16px;
}

.color-option {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s ease;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.color-option:hover {
    transform: scale(1.1);
    border-color: #000;
}

.color-option.selected {
    border-color: #000;
    border-width: 3px;
    transform: scale(1.1);
}

.color-option.selected::after {
    content: '✓';
    color: white;
    font-size: 16px;
    font-weight: bold;
    text-shadow: 1px 1px 2px rgba(0,0,0,0.8);
}

/* Special styling for black color option to show it's selected */
.color-option[style*="rgb(0, 0, 0)"].selected::after,
.color-option[style*="#000000"].selected::after,
.color-option[style*="#000"].selected::after {
    color: white;
    text-shadow: 1px 1px 2px rgba(255,255,255,0.5);
}

.location-color-preview {
    display: inline-block;
    width: 20px;
    height: 20px;
    border-radius: 4px;
    margin-right: 8px;
    vertical-align: middle;
    border: 1px solid rgba(0,0,0,0.1);
}

/* Print Styles */
@media print {
  /* Hide non-essential elements */
  .no-print {
    display: none !important;
  }
  
  /* Page setup */
  @page {
    margin: 0.5in;
    size: landscape;
  }
  
  body {
    font-size: 12px;
    line-height: 1.4;
    color: #000 !important;
    background: white !important;
  }
  
  /* Print header */
  .print-header {
    display: block !important;
    text-align: center;
    margin-bottom: 20px;
    border-bottom: 2px solid #000;
    padding-bottom: 10px;
  }
  
  .print-title {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 5px;
  }
  
  .print-date-range {
    font-size: 16px;
    color: #666;
  }
  
  /* Table styles for print */
  .print-table {
    width: 100%;
    border-collapse: collapse;
    margin-top: 10px;
  }
  
  .print-table th,
  .print-table td {
    border: 1px solid #000;
    padding: 8px;
    text-align: left;
    vertical-align: top;
  }
  
  .print-table th {
    background-color: #f0f0f0 !important;
    font-weight: bold;
    text-align: center;
  }
  
  .print-staff-name {
    font-weight: bold;
    background-color: #f8f8f8 !important;
  }
  
  .print-shift {
    margin-bottom: 5px;
    padding: 3px;
    border: 1px solid #ccc;
    border-radius: 3px;
    background-color: #f9f9f9 !important;
  }
  
  .print-shift-time {
    font-weight: bold;
    font-size: 11px;
  }
  
  .print-shift-location {
    font-size: 10px;
    color: #666;
  }
  
  .print-shift-notes {
    font-size: 9px;
    font-style: italic;
    color: #888;
    margin-top: 2px;
  }
  
  .print-shift-breaks {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px dashed #ddd;
  }
  
  .print-break {
    font-size: 9px;
    color: #444;
    display: flex;
    align-items: center;
    gap: 3px;
    margin-top: 1px;
  }
  
  .print-timeoff {
    background-color: #ffe6e6 !important;
    border: 1px solid #ff9999;
    padding: 2px 4px;
    margin: 2px 0;
    font-size: 9px;
    text-align: center;
  }
  
  .print-totals {
    background-color: #e6f3ff !important;
    font-weight: bold;
  }
  
  .print-footer {
    margin-top: 20px;
    text-align: center;
    font-size: 10px;
    color: #666;
    border-top: 1px solid #ccc;
    padding-top: 10px;
  }
  
  /* Monthly view specific */
  .print-monthly-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 1px;
    border: 2px solid #000;
  }
  
  .print-monthly-cell {
    border: 1px solid #ccc;
    padding: 5px;
    min-height: 80px;
    background: white !important;
  }
  
  .print-monthly-date {
    font-weight: bold;
    margin-bottom: 3px;
  }
  
  .print-monthly-shift {
    font-size: 8px;
    margin: 1px 0;
    padding: 1px 2px;
    background-color: #f0f0f0 !important;
    border: 1px solid #ddd;
  }
  
  .print-monthly-shift-breaks {
    margin-top: 2px;
    border-top: 1px dotted #ccc;
    padding-top: 2px;
  }
  
  .print-monthly-break {
    font-size: 7.5px;
    color: #555;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

/* Hide print header by default */
.print-header {
  display: none;
}

.schedule-container {
    position: relative;
}

.timeoff-tooltip {
    position: absolute;
    background: rgba(0, 0, 0, 0.9);
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 11px;
    white-space: nowrap;
    z-index: 1000;
    opacity: 0;
    transform: translateY(-8px);
    transition: all 0.3s ease;
    pointer-events: none;
    max-width: 200px;
    text-align: center;
}

.timeoff-tooltip.show {
    opacity: 1;
    transform: translateY(0);
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
    .timeoff-indicator {
        font-size: 8px;
        padding: 2px 4px;
        letter-spacing: 0.3px;
        min-height: 16px;
    }
    
    .timeoff-indicator .external-icon {
        width: 8px;
        height: 8px;
    }
    
    .timeoff-tooltip {
        font-size: 10px;
        padding: 6px 8px;
        max-width: 150px;
    }
    
    .color-palette {
        grid-template-columns: repeat(4, 1fr);
    }
    
    .color-option {
        width: 35px;
        height: 35px;
    }
}

@media (max-width: 480px) {
    .timeoff-indicator {
        font-size: 7px;
        padding: 1px 3px;
        margin: 1px 0;
        min-height: 14px;
    }
    
    .timeoff-indicator .external-icon {
        width: 6px;
        height: 6px;
    }
    
    .timeoff-tooltip {
        font-size: 9px;
        padding: 4px 6px;
        max-width: 120px;
    }
    
    .color-palette {
        grid-template-columns: repeat(3, 1fr);
    }
    
    .color-option {
        width: 30px;
        height: 30px;
    }
}
`;

// ===== Constants =====
const HOURS = [...Array(24)].map((_, i) => i.toString().padStart(2, '0'));
const MINUTES = [...Array(60)].map((_, i) => i.toString().padStart(2, '0'));
const RENDER_REPEAT = 6;

// ===== Utility Functions =====
const isValidTime = (h, m) => {
  const hh = parseInt(h, 10);
  const mm = parseInt(m, 10);
  return !isNaN(hh) && !isNaN(mm) && hh >= 0 && hh < 24 && mm >= 0 && mm < 60;
};

const TimeColumn = ({ values, selected, onSelect, columnRef, label }) => (
  <div className="zoneStaff_timePicker_column" ref={columnRef} role="listbox" aria-label={label}>
    {Array.from({ length: RENDER_REPEAT }).map((_, i) => (
      <React.Fragment key={i}>
        {values.map((val) => (
          <div
            key={`${i}-${val}`}
            className={`zoneStaff_timePicker_item ${selected === val ? 'selected' : ''}`}
            onClick={() => onSelect(val)}
            role="option"
            aria-selected={selected === val}
          >
            {val}
          </div>
        ))}
      </React.Fragment>
    ))}
  </div>
);

// Custom TimePicker Component
const TimePicker = ({ onChange, value, timeFormat = '24h' }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState(value || '09:00');
  const [selectedHour, setSelectedHour] = useState(value ? value.split(':')[0] : '09');
  const [selectedMinute, setSelectedMinute] = useState(value ? value.split(':')[1] : '00');
  const [tempHour, setTempHour] = useState(null);
  const [tempMinute, setTempMinute] = useState(null);

  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const iconRef = useRef(null);

  useEffect(() => {
    if (value) {
      // Convert value to display format based on timeFormat setting
      const displayValue = timeFormat === '12h' ? convert24To12(value) : value;
      setSelectedTime(displayValue);

      // Always store internally in 24-hour format
      const [h, m] = value.split(':');
      setSelectedHour(h);
      setSelectedMinute(m);
    }
  }, [value, timeFormat]);

  const toggleDropdown = useCallback(() => {
    setIsOpen((prev) => !prev);
    setTempHour(null);
    setTempMinute(null);
  }, []);

  const handleClickOutside = useCallback((e) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target) &&
      inputRef.current &&
      !inputRef.current.contains(e.target) &&
      iconRef.current &&
      !iconRef.current.contains(e.target)
    ) {
      if (tempHour !== null && tempMinute === null) {
        const h = tempHour.toString().padStart(2, '0');
        const m = selectedMinute;
        const timeValue = `${h}:${m}`;
        setSelectedHour(h);
        setSelectedMinute(m);
        setSelectedTime(timeValue);
        if (onChange) onChange(timeValue);
      }
      else if (tempMinute !== null && tempHour === null) {
        const h = selectedHour;
        const m = tempMinute.toString().padStart(2, '0');
        const timeValue = `${h}:${m}`;
        setSelectedHour(h);
        setSelectedMinute(m);
        setSelectedTime(timeValue);
        if (onChange) onChange(timeValue);
      }
      setIsOpen(false);
      setTempHour(null);
      setTempMinute(null);
    }
  }, [tempHour, tempMinute, selectedHour, selectedMinute, onChange]);

  const handleTimeSelect = useCallback((hour, minute) => {
    if (hour !== null && minute === null) {
      setTempHour(hour);
      const h = hour.toString().padStart(2, '0');
      const m = selectedMinute;
      const timeValue = `${h}:${m}`;

      // Display in selected format, but always pass 24-hour format to onChange
      const displayValue = timeFormat === '12h' ? convert24To12(timeValue) : timeValue;
      setSelectedTime(displayValue);

      if (tempMinute !== null) {
        setSelectedHour(h);
        setSelectedMinute(m);
        if (onChange) onChange(timeValue); // Always pass 24-hour format
        setIsOpen(false);
        setTempHour(null);
        setTempMinute(null);
        inputRef.current?.focus();
      }
      return;
    }

    if (minute !== null && hour === null) {
      setTempMinute(minute);
      const h = tempHour ? tempHour.toString().padStart(2, '0') : selectedHour;
      const m = minute.toString().padStart(2, '0');
      const timeValue = `${h}:${m}`;

      // Display in selected format, but always pass 24-hour format to onChange
      const displayValue = timeFormat === '12h' ? convert24To12(timeValue) : timeValue;
      setSelectedTime(displayValue);

      if (tempHour !== null) {
        setSelectedHour(h);
        setSelectedMinute(m);
        if (onChange) onChange(timeValue); // Always pass 24-hour format
        setIsOpen(false);
        setTempHour(null);
        setTempMinute(null);
        inputRef.current?.focus();
      }
      return;
    }

    if (minute !== null && hour !== null) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      setSelectedHour(h);
      setSelectedMinute(m);
      const timeValue = `${h}:${m}`;

      // Display in selected format, but always pass 24-hour format to onChange
      const displayValue = timeFormat === '12h' ? convert24To12(timeValue) : timeValue;
      setSelectedTime(displayValue);
      if (onChange) onChange(timeValue); // Always pass 24-hour format
      setIsOpen(false);
      setTempHour(null);
      setTempMinute(null);
      inputRef.current?.focus();
    }
  }, [onChange, tempHour, tempMinute, selectedMinute, selectedHour, timeFormat]);

  const handleInputChange = (e) => {
    const value = e.target.value;

    if (timeFormat === '12h') {
      // Handle 12-hour format input
      if (!/^[0-9:APM\s]*$/i.test(value)) return;

      // Parse 12-hour format
      const match = value.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
      if (match) {
        const [_, hours, minutes, period] = match;
        const h = parseInt(hours);
        const m = parseInt(minutes);

        if (h > 12 || m > 59) return;

        // Convert to 24-hour format for internal storage
        let hour24 = h;
        if (period.toUpperCase() === 'PM' && h !== 12) {
          hour24 = h + 12;
        } else if (period.toUpperCase() === 'AM' && h === 12) {
          hour24 = 0;
        }

        const timeValue = `${hour24.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
        setSelectedTime(value);
        setSelectedHour(hour24.toString().padStart(2, '0'));
        setSelectedMinute(m.toString().padStart(2, '0'));
        if (onChange) onChange(timeValue); // Always pass 24-hour format
      } else {
        setSelectedTime(value);
      }
    } else {
      // Handle 24-hour format input
      if (!/^[0-9:]*$/.test(value) || (value.match(/:/g) || []).length > 1) return;
      const [h = '', m = ''] = value.split(':');
      if (h.length > 2 || m.length > 2) return;
      const hours = parseInt(h, 10);
      const minutes = parseInt(m, 10);
      if (hours > 23 || minutes > 59) return;
      let formattedTime = value;
      if (value.length >= 2 && !value.includes(':')) {
        formattedTime = value.slice(0, 2) + ':' + value.slice(2);
      }
      setSelectedTime(formattedTime);
      if (formattedTime.includes(':') && isValidTime(h, m)) {
        setSelectedHour(h.padStart(2, '0'));
        setSelectedMinute(m.padStart(2, '0'));
        if (onChange) onChange(formattedTime);
      }
    }
  };

  const scrollToSelected = useCallback(() => {
    const scrollTo = (ref, value) => {
      if (!ref.current) return;
      const items = ref.current.querySelectorAll(`.selected`);
      if (items.length) {
        const el = items[Math.floor(items.length / 2)];
        ref.current.scrollTop = el.offsetTop - ref.current.clientHeight / 2 + el.offsetHeight / 2;
      }
    };
    scrollTo(hourRef, tempHour || selectedHour);
    scrollTo(minuteRef, tempMinute || selectedMinute);
  }, [selectedHour, selectedMinute, tempHour, tempMinute]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  useEffect(() => {
    if (isOpen) scrollToSelected();
  }, [isOpen, scrollToSelected]);

  return (
    <div className="zoneStaff_timePicker_container">
      <div className="zoneStaff_timePicker_inputContainer">
        <input
          ref={inputRef}
          type="text"
          className="zoneStaff_timePicker_input"
          placeholder="09:00"
          value={selectedTime}
          onFocus={() => inputRef.current?.select()}
          onChange={handleInputChange}
          onClick={toggleDropdown}
          aria-label={t('select_time')}
        />
        <div
          ref={iconRef}
          onClick={toggleDropdown}
          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
        >
          <Icon source={ClockIcon} tone="base" />
        </div>
      </div>
      {isOpen && (
        <div
          className="zoneStaff_timePicker_dropdown"
          ref={dropdownRef}
          role="dialog"
          aria-modal="true"
        >
          <div className="zoneStaff_timePicker_highlighted_row" />
          <TimeColumn
            values={HOURS}
            selected={tempHour || selectedHour}
            onSelect={(h) => handleTimeSelect(h, null)}
            columnRef={hourRef}
            label={t('hours')}
          />
          <div className="zoneStaff_timePicker_colon">:</div>
          <TimeColumn
            values={MINUTES}
            selected={tempMinute || selectedMinute}
            onSelect={(m) => handleTimeSelect(null, m)}
            columnRef={minuteRef}
            label={t('minutes')}
          />
        </div>
      )}
    </div>
  );
};

// Color Picker Component
const ColorPicker = ({ selectedColor, onColorChange, colors }) => {
  return (
    <div className="color-palette">
      {colors.map((color, index) => (
        <div
          key={index}
          className={`color-option ${selectedColor === color.value ? 'selected' : ''}`}
          style={{ backgroundColor: color.value }}
          onClick={() => onColorChange(color)}
          title={color.name}
        />
      ))}
    </div>
  );
};

// Color Settings Modal (handles both locations and staff)
const ColorSettingsModal = ({ active, onClose, locations, staffList, onUpdateLocationColor, onUpdateStaffColor }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('location'); // 'location' or 'staff'
  const [selectedId, setSelectedId] = useState('');
  const [selectedColor, setSelectedColor] = useState(() => getDefaultLocationColor(t));
  const [isUpdating, setIsUpdating] = useState(false);

  // Memoize color palettes
  const LOCATION_COLORS = useMemo(() => getLocationColors(t), [t]);
  const DEFAULT_LOCATION_COLOR = useMemo(() => getDefaultLocationColor(t), [t]);

  // Find the selected entity (location or staff)
  const selectedEntity = useMemo(() => {
    if (mode === 'location') {
      return locations.find(loc => loc.id.toString() === selectedId.toString());
    } else {
      return staffList.find(staff => staff.id.toString() === selectedId.toString());
    }
  }, [mode, selectedId, locations, staffList]);

  // Reset selected entity ID when mode changes
  useEffect(() => {
    setSelectedId('');
    setSelectedColor(DEFAULT_LOCATION_COLOR);
  }, [mode, DEFAULT_LOCATION_COLOR]);

  // Set the color picker state when a new entity is selected
  useEffect(() => {
    if (selectedEntity) {
      const entityColor = selectedEntity.color
        ? LOCATION_COLORS.find(c => c.value === selectedEntity.color) || DEFAULT_LOCATION_COLOR
        : DEFAULT_LOCATION_COLOR;
      setSelectedColor(entityColor);
    } else {
      setSelectedColor(DEFAULT_LOCATION_COLOR);
    }
  }, [selectedId, selectedEntity, LOCATION_COLORS, DEFAULT_LOCATION_COLOR]);

  const handleUpdateColor = async () => {
    if (!selectedId || !selectedColor) return;

    setIsUpdating(true);
    try {
      if (mode === 'location') {
        await onUpdateLocationColor(selectedId, selectedColor.value);
        shopify.toast.show(`${t('location_color_updated_to') || 'Location color updated to'} ${selectedColor.name}`);
      } else {
        await onUpdateStaffColor(selectedId, selectedColor.value);
        shopify.toast.show(`${t('staff_color_updated_to') || 'Staff color updated to'} ${selectedColor.name}`);
      }
      onClose();
    } catch (error) {
      shopify.toast.show(t('failed_to_update_color') || 'Failed to update color', { error: true });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleClose = () => {
    setSelectedId('');
    setSelectedColor(DEFAULT_LOCATION_COLOR);
    setMode('location');
    onClose();
  };

  const entityOptions = useMemo(() => {
    if (mode === 'location') {
      return locations.map(location => ({
        label: location.name,
        value: location.id.toString()
      }));
    } else {
      return staffList.map(staff => ({
        label: `${staff.first_name} ${staff.last_name}`,
        value: staff.id.toString()
      }));
    }
  }, [mode, locations, staffList]);

  return (
    <Modal
      open={active}
      onClose={handleClose}
      title={t('customize_colors') || 'Customize Colors'}
      primaryAction={{
        content: t('update_color'),
        onAction: handleUpdateColor,
        loading: isUpdating,
        disabled: !selectedId || !selectedColor || isUpdating
      }}
      secondaryActions={[{
        content: t('cancel'),
        onAction: handleClose,
      }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Select
            label={t('customize_colors_for') || 'Customize Colors For'}
            options={[
              { label: t('locations') || 'Locations', value: 'location' },
              { label: t('staff') || 'Staff', value: 'staff' }
            ]}
            onChange={setMode}
            value={mode}
          />

          <Select
            label={mode === 'location' ? (t('select_location') || 'Select Location') : (t('select_staff') || 'Select Staff')}
            options={entityOptions}
            onChange={setSelectedId}
            value={selectedId}
            placeholder={mode === 'location' ? (t('choose_a_location_to_customize') || 'Choose a location') : (t('choose_a_staff_to_customize') || 'Choose a staff member')}
          />

          {selectedEntity && (
            <Box>
              <Text variant="headingMd">{t('current_color')}</Text>
              <Box paddingBlockStart="200">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div
                    className="location-color-preview"
                    style={{
                      backgroundColor: selectedEntity.color || DEFAULT_LOCATION_COLOR.value,
                      width: '30px',
                      height: '30px',
                      borderRadius: '6px'
                    }}
                  />
                  <Text>
                    {LOCATION_COLORS.find(c => c.value === selectedEntity.color)?.name || DEFAULT_LOCATION_COLOR.name}
                  </Text>
                </div>
              </Box>
            </Box>
          )}

          {selectedId && (
            <Box>
              <Text variant="headingMd" paddingBlockEnd="200">{t('choose_new_color')}</Text>
              <ColorPicker
                selectedColor={selectedColor.value}
                onColorChange={setSelectedColor}
                colors={LOCATION_COLORS}
              />
            </Box>
          )}

          {selectedColor && selectedId && (
            <Box>
              <Text variant="headingMd">{t('preview')}</Text>
              <Box paddingBlockStart="200">
                <div
                  style={{
                    backgroundColor: selectedColor.value,
                    color: selectedColor.text,
                    padding: '8px 12px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}
                >
                  09:00 - 17:00<br />
                  {mode === 'location' ? `Staff Name (${selectedEntity?.name})` : `${selectedEntity?.first_name} ${selectedEntity?.last_name} (Location)`}
                </div>
              </Box>
            </Box>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

// Copy Week Modal Component
const CopyWeekModal = ({ active, onClose, currentStartDate, onCopyWeek }) => {
  const { t } = useTranslation();
  const [targetDate, setTargetDate] = useState('');
  const [clearTarget, setClearTarget] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCopy = async () => {
    if (!targetDate) return;

    setIsSubmitting(true);
    try {
      await onCopyWeek(targetDate, clearTarget);
      onClose();
    } catch (error) {
      // Error handled silently
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTargetDate('');
    setClearTarget(false);
    onClose();
  };

  const currentWeekStart = new Date(currentStartDate);
  const formattedCurrentWeek = currentWeekStart.toLocaleDateString();

  return (
    <Modal
      open={active}
      onClose={handleClose}
      title={t('copy_weekly_schedule')}
      primaryAction={{
        content: t('copy_shifts'),
        onAction: handleCopy,
        loading: isSubmitting,
        disabled: !targetDate || isSubmitting
      }}
      secondaryActions={[{
        content: t('cancel'),
        onAction: handleClose,
      }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p">
            {t('copying_shifts_from_week_of')} <strong>{formattedCurrentWeek}</strong>.
          </Text>

          <Box>
            <TextField
              label={t('select_target_week_any_date_within_week')}
              type="date"
              value={targetDate}
              onChange={setTargetDate}
              helpText={t('shifts_will_be_copied_to_the_week_containing_this_date')}
            />
          </Box>

          <Checkbox
            label={t('clear_existing_shifts_in_target_week_before_copying')}
            checked={clearTarget}
            onChange={setClearTarget}
            helpText={t('if_unchecked_new_shifts_will_be_added_alongside_existing_ones')}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

// Delete All Shifts Modal
const DeleteAllShiftsModal = ({ active, onClose, staffList, onDelete }) => {
  const { t } = useTranslation();
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Toggle selection for a single staff
  const handleToggleStaff = (staffId) => {
    setSelectedStaffIds(prev =>
      prev.includes(staffId)
        ? prev.filter(id => id !== staffId)
        : [...prev, staffId]
    );
  };

  // Select/Deselect all
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStaffIds(staffList.map(s => s.id));
    } else {
      setSelectedStaffIds([]);
    }
  };

  const handleDelete = async () => {
    if (selectedStaffIds.length === 0) return;

    setIsSubmitting(true);
    try {
      await onDelete(selectedStaffIds);
      setSelectedStaffIds([]); // Reset selection on success
      onClose();
    } catch (error) {
      // Error handling usually done in parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedStaffIds([]);
    onClose();
  };

  const areAllSelected = staffList.length > 0 && selectedStaffIds.length === staffList.length;

  return (
    <Modal
      open={active}
      onClose={handleClose}
      title={t('delete_all_shifts') || 'Delete All Shifts'}
      primaryAction={{
        content: t('delete_shifts') || 'Delete Shifts',
        onAction: handleDelete,
        destructive: true,
        loading: isSubmitting,
        disabled: selectedStaffIds.length === 0 || isSubmitting
      }}
      secondaryActions={[{
        content: t('cancel') || 'Cancel',
        onAction: handleClose,
      }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Banner tone="critical">
            <Text as="p">
              {t('delete_all_shifts_warning') || 'Warning: This will permanently delete ALL past, present, and future shifts for the selected staff members. This action cannot be undone.'}
            </Text>
          </Banner>

          <Box paddingBlockStart="200">
            <Checkbox
              label={t('select_all_staff') || "Select All Staff"}
              checked={areAllSelected}
              onChange={handleSelectAll}
            />
          </Box>

          <Box paddingBlockStart="200" style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <BlockStack gap="200">
              {staffList.map(staff => (
                <Checkbox
                  key={staff.id}
                  label={`${staff.first_name} ${staff.last_name}`}
                  checked={selectedStaffIds.includes(staff.id)}
                  onChange={() => handleToggleStaff(staff.id)}
                />
              ))}
            </BlockStack>
          </Box>

          <Text tone="subdued">
            {selectedStaffIds.length} {t('staff_selected') || 'staff selected'}
          </Text>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default function Schedule() {
  const { t } = useTranslation();
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const [copyModalActive, setCopyModalActive] = useState(false);
  const [deleteShiftsModalActive, setDeleteShiftsModalActive] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [colorBy, setColorBy] = useState(() => {
    return localStorage.getItem('schedule_color_by') || 'location';
  });

  const handleColorByChange = useCallback((value) => {
    setColorBy(value);
    localStorage.setItem('schedule_color_by', value);
  }, []);

  const [colorByPopoverActive, setColorByPopoverActive] = useState(false);
  const toggleColorByPopoverActive = useCallback(
    () => setColorByPopoverActive((active) => !active),
    [],
  );
  const [showClearShiftsTooltip, setShowClearShiftsTooltip] = useState(() => {
    return localStorage.getItem('hide_clear_shifts_tooltip') !== 'true';
  });

  const handleDismissTooltip = (e) => {
    e.stopPropagation();
    setShowClearShiftsTooltip(false);
    localStorage.setItem('hide_clear_shifts_tooltip', 'true');
  };
  // Helper function to get location color from localStorage
  const getLocationColor = (locationId, locations) => {
    const storedColors = getLocationColorsFromStorage();
    const location = locations.find(loc => loc.id.toString() === locationId.toString());
    const LOCATION_COLORS = getLocationColors(t);
    const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);

    // Check if we have a stored color for this location
    const storedColor = storedColors[locationId.toString()];
    if (storedColor) {
      const colorInfo = LOCATION_COLORS.find(c => c.value === storedColor) || DEFAULT_LOCATION_COLOR;
      return colorInfo;
    }

    // If no stored color, assign black as default
    const assignedColor = assignColorToLocation(locationId, storedColors, t);
    const colorInfo = LOCATION_COLORS.find(c => c.value === assignedColor) || DEFAULT_LOCATION_COLOR;
    return colorInfo;
  };

  // Helper function to get staff color from localStorage
  const getStaffColor = (staffId, staffList) => {
    const storedColors = getStaffColorsFromStorage();
    const LOCATION_COLORS = getLocationColors(t);
    const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);

    // Check if we have a stored color for this staff member
    const storedColor = storedColors[staffId.toString()];
    if (storedColor) {
      const colorInfo = LOCATION_COLORS.find(c => c.value === storedColor) || DEFAULT_LOCATION_COLOR;
      return colorInfo;
    }

    // If no stored color, assign black as default
    const assignedColor = assignColorToStaff(staffId, storedColors, t);
    const colorInfo = LOCATION_COLORS.find(c => c.value === assignedColor) || DEFAULT_LOCATION_COLOR;
    return colorInfo;
  };
  const [repeatMonth, setRepeatMonth] = useState(false);
  const [startsAt, setStartsAt] = useState('09:00');
  const [endsAt, setEndsAt] = useState('17:00');
  const [location, setLocation] = useState('');
  const [active, setActive] = useState(false);
  const [colorModalActive, setColorModalActive] = useState(false);
  const [breaks, setBreaks] = useState([{ name: '', type: '', startsAt: '13:30', endsAt: '14:30' }]);
  const [staff, setStaff] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [repeatShift, setRepeatShift] = useState(false);
  const [selectedView, setSelectedView] = useState('weekly');
  const [selectedDate, setSelectedDate] = useState({ start: new Date(), end: new Date() });
  const [{ month, year }, setDateState] = useState({
    month: new Date().getMonth(),
    year: new Date().getFullYear(),
  });
  const [schedules, setSchedules] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [prevSelectedDate, setPrevSelectedDate] = useState(null);
  const [showBreaks, setShowBreaks] = useState(false);
  const [settings, setSettings] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [repeatYear, setRepeatYear] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [notes, setNotes] = useState('');
  const [timeFormat, setTimeFormat] = useState('24h'); // Default to 24-hour format
  const currentYear = new Date().getFullYear();

  // Availability edit modal state
  const [availabilityModal, setAvailabilityModal] = useState({
    open: false,
    staffId: null,
    staffName: '',
    availability: {},
  });
  const [isSavingAvailability, setIsSavingAvailability] = useState(false);

  const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const DEFAULT_AVAILABILITY = {
    monday: { available: true },
    tuesday: { available: true },
    wednesday: { available: true },
    thursday: { available: true },
    friday: { available: true },
    saturday: { available: true },
    sunday: { available: false },
  };

  const parseAvailability = (raw) => {
    try {
      if (!raw) return DEFAULT_AVAILABILITY;
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const result = {};
      DAY_KEYS.forEach(day => {
        result[day] = parsed[day] ?? DEFAULT_AVAILABILITY[day];
      });
      return result;
    } catch {
      return DEFAULT_AVAILABILITY;
    }
  };

  const openAvailabilityModal = (staffMember) => {
    setAvailabilityModal({
      open: true,
      staffId: staffMember.id,
      staffName: `${staffMember.first_name} ${staffMember.last_name}`,
      availability: { ...parseAvailability(staffMember.days_availability) },
    });
  };

  const handleAvailabilityToggle = (day) => {
    setAvailabilityModal(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { available: !prev.availability[day]?.available },
      },
    }));
  };

  const handleSaveAvailability = async () => {
    setIsSavingAvailability(true);
    try {
      const res = await axiosClient.post(
        `/update-staff-availability/${availabilityModal.staffId}`,
        { availability: availabilityModal.availability }
      );
      if (res.data.success) {
        // Update local staffList so the badge reflects immediately
        setStaffList(prev => prev.map(s =>
          s.id === availabilityModal.staffId
            ? { ...s, days_availability: JSON.stringify(availabilityModal.availability) }
            : s
        ));
        setAvailabilityModal(prev => ({ ...prev, open: false }));
      }
    } catch (e) {
      // Error handled silently
    } finally {
      setIsSavingAvailability(false);
    }
  };


  const handleCopyWeek = async (targetDate, clearTarget) => {
    try {
      const response = await axiosClient.post('/copy_week_schedule', {
        source_date: formatDateForBackend(selectedDate.start),
        target_date: targetDate,
        clear_target: clearTarget
      });

      if (response.data.success) {
        shopify.toast.show(response.data.message);
        // Optional: If user copied to the current view or a view they navigate to, refreshing is good
        // But usually, they copy to a future week.
      } else {
        shopify.toast.show(response.data.message || t('failed_to_copy_schedule'), { error: true });
      }
    } catch (error) {
      shopify.toast.show(t('failed_to_copy_schedule'), { error: true });
    }
  };

  const handleBulkDeleteShifts = async (staffIds) => {
    try {
      const response = await axiosClient.post('/bulk_destroy_schedules', {
        staff_ids: staffIds
      });

      if (response.data.success) {
        shopify.toast.show(response.data.message || t('shifts_deleted_successfully'));

        // Refresh Data
        if (selectedView === 'weekly') {
          const currentWeekStart = new Date(selectedDate.start);
          const currentWeekEnd = new Date(selectedDate.end);
          await fetchSchedules(currentWeekStart, currentWeekEnd);
        } else if (selectedView === 'monthly' || selectedView === 'yearly') {
          const currentMonthStart = new Date(year, month, 1);
          const currentMonthEnd = new Date(year, month + 1, 0);
          await fetchSchedules(currentMonthStart, currentMonthEnd);
        }
      } else {
        shopify.toast.show(response.data.message || t('failed_to_delete_shifts'), { error: true });
      }
    } catch (error) {
      shopify.toast.show(t('failed_to_delete_shifts'), { error: true });
      throw error; // Re-throw to be caught by modal
    }
  };
  // Print functionality
  const handlePrint = () => {
    // Create print content
    const printContent = generatePrintContent();

    // Create a new window for printing
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Schedule - ${getDateRangeText()}</title>
          <style>
            ${timePickerStyles}
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);

    printWindow.document.close();

    // Wait for content to load then print
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  // Generate print content based on current view
  const generatePrintContent = () => {
    const dateRange = getDateRangeText();

    if (selectedView === 'monthly' || selectedView === 'yearly') {
      return generateMonthlyPrintContent(dateRange);
    } else {
      return generateWeeklyPrintContent(dateRange);
    }
  };

  // Get formatted date range text
  const getDateRangeText = () => {
    if (selectedView === 'weekly') {
      const startDate = selectedDate.start.toLocaleDateString();
      const endDate = selectedDate.end.toLocaleDateString();
      return `${startDate} - ${endDate}`;
    } else {
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      return `${monthNames[month]} ${year}`;
    }
  };

  // Generate weekly print content
  const generateWeeklyPrintContent = (dateRange) => {
    let content = `
      <div class="print-header">
        <div class="print-title">Weekly Schedule</div>
        <div class="print-date-range">${dateRange}</div>
      </div>
      
      <table class="print-table">
        <thead>
          <tr>
            <th>Staff Member</th>
    `;

    // Add day headers
    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedDate.start);
      date.setDate(selectedDate.start.getDate() + i);
      const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayNumber = date.getDate();
      content += `<th>${dayName} ${dayNumber}</th>`;
    }

    content += `
          </tr>
        </thead>
        <tbody>
    `;

    // Add staff rows
    staffList.forEach(staffMember => {
      content += `
        <tr>
          <td class="print-staff-name">${staffMember.first_name} ${staffMember.last_name}</td>
      `;

      // Add daily schedules
      for (let i = 0; i < 7; i++) {
        const dayDate = new Date(selectedDate.start);
        dayDate.setDate(dayDate.getDate() + i);
        const daySchedules = schedules.filter(schedule =>
          schedule.staff_id === staffMember.id &&
          schedule.start_date === formatDateForBackend(dayDate)
        );

        const staffTimeOffs = getTimeOffForStaffAndDate(staffMember.id, formatDateForBackend(dayDate));

        content += '<td>';

        // Add time-offs
        staffTimeOffs.forEach(timeOff => {
          const statusText = timeOff.status?.toLowerCase() === 'approved' ? 'Approved Time Off' :
            timeOff.status?.toLowerCase() === 'rejected' ? 'Rejected Time Off' : 'Time Off Request';
          content += `<div class="print-timeoff">${statusText}</div>`;
        });

        // Add shifts
        daySchedules.forEach(schedule => {
          const location = locations.find(l => l.id.toString() === schedule.location_id.toString());
          
          let breaksContent = '';
          if (schedule.breaks && schedule.breaks.length > 0) {
            breaksContent = '<div class="print-shift-breaks">';
            schedule.breaks.forEach(br => {
              const breakTypeStr = br.type ? ` (${t(br.type.toLowerCase())})` : '';
              const breakNameStr = br.name ? `: ${br.name}` : '';
              breaksContent += `<div class="print-break">☕ ${t('break_time')}${breakNameStr} (${removeSeconds(br.start_time)} - ${removeSeconds(br.end_time)})${breakTypeStr}</div>`;
            });
            breaksContent += '</div>';
          }

          content += `
            <div class="print-shift">
              <div class="print-shift-time">${removeSeconds(schedule.start_time)} - ${removeSeconds(schedule.end_time)}</div>
              <div class="print-shift-location">${location?.name || ''}</div>
              ${schedule.notes ? `<div class="print-shift-notes">📝 ${schedule.notes}</div>` : ''}
              ${breaksContent}
            </div>
          `;
        });

        content += '</td>';
      }

      content += '</tr>';
    });

    // Add totals row
    content += `
      <tr class="print-totals">
        <td><strong>Daily Totals</strong></td>
    `;

    for (let i = 0; i < 7; i++) {
      const date = new Date(selectedDate.start);
      date.setDate(selectedDate.start.getDate() + i);
      const daySchedules = schedules.filter(schedule =>
        schedule.start_date === formatDateForBackend(date)
      );

      const totalShifts = daySchedules.length;
      const totalHours = calculateTotalHours(daySchedules);
      const totalPay = calculateTotalPay(daySchedules, 20);

      content += `
        <td>
          <div>👥 ${totalShifts} shifts</div>
          <div>🕒 ${totalHours} hours</div>
          <div>💰 $${totalPay}</div>
        </td>
      `;
    }

    content += `
          </tr>
        </tbody>
      </table>
      
      <div class="print-footer">
        <div>Generated on ${new Date().toLocaleString()}</div>
        <div>Staff Management System</div>
      </div>
    `;

    return content;
  };

  // Generate monthly print content
  const generateMonthlyPrintContent = (dateRange) => {
    let content = `
      <div class="print-header">
        <div class="print-title">Monthly Schedule</div>
        <div class="print-date-range">${dateRange}</div>
      </div>
      
      <div class="print-monthly-grid">
    `;

    // Add day headers
    ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].forEach(day => {
      content += `<div class="print-monthly-cell"><strong>${day}</strong></div>`;
    });

    // Generate calendar days
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    let startDay = firstDayOfMonth.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;

    const previousMonthLastDay = new Date(year, month, 0).getDate();
    let currentDay = 1;
    let previousMonthDay = previousMonthLastDay - startDay + 1;

    // Helper function to parse date string properly
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    for (let i = 0; i < 6; i++) {
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < startDay) {
          content += `<div class="print-monthly-cell" style="opacity: 0.3;">${previousMonthDay++}</div>`;
        } else if (currentDay > daysInMonth) {
          content += `<div class="print-monthly-cell" style="opacity: 0.3;">${currentDay++ - daysInMonth}</div>`;
        } else {
          const currentDate = new Date(year, month, currentDay);
          const daySchedules = schedules.filter(schedule =>
            schedule.start_date === formatDateForBackend(currentDate)
          );

          // Get time-offs for this date
          const dayTimeOffs = timeOffs.filter(timeOff => {
            const timeOffStart = parseDate(timeOff.start_date);
            const timeOffEnd = parseDate(timeOff.end_date);
            const currentDateForComparison = new Date(year, month, currentDay);

            timeOffStart.setHours(0, 0, 0, 0);
            timeOffEnd.setHours(23, 59, 59, 999);
            currentDateForComparison.setHours(0, 0, 0, 0);

            const isWithinTimeOffPeriod = currentDateForComparison >= timeOffStart && currentDateForComparison <= timeOffEnd;
            const isNotDeclined = !['declined', 'rejected'].includes(timeOff.status?.toLowerCase());

            return isWithinTimeOffPeriod && isNotDeclined;
          });

          content += `<div class="print-monthly-cell">`;
          content += `<div class="print-monthly-date">${currentDay}</div>`;

          // Add time-offs
          dayTimeOffs.forEach(timeOff => {
            const statusText = timeOff.status?.toLowerCase() === 'approved' ? 'Time Off' : 'Time Off Req';
            content += `<div class="print-timeoff">${statusText}: ${timeOff.first_name}</div>`;
          });

          // Add shifts
          daySchedules.forEach(schedule => {
            const staff = staffList.find(s => s.id === schedule.staff_id);
            const location = locations.find(l => l.id.toString() === schedule.location_id.toString());
            
            let breaksContent = '';
            if (schedule.breaks && schedule.breaks.length > 0) {
              breaksContent = '<div class="print-monthly-shift-breaks">';
              schedule.breaks.forEach(br => {
                const breakName = br.name ? `${br.name}` : t('break_time');
                breaksContent += `<div class="print-monthly-break">☕ ${breakName}: ${removeSeconds(br.start_time)}-${removeSeconds(br.end_time)}</div>`;
              });
              breaksContent += '</div>';
            }

            content += `
              <div class="print-monthly-shift">
                <div>${removeSeconds(schedule.start_time)}-${removeSeconds(schedule.end_time)}</div>
                <div>${staff?.first_name || ''} (${location?.name || ''})</div>
                ${breaksContent}
              </div>
            `;
          });

          content += '</div>';
          currentDay++;
        }
      }
    }

    content += `
      </div>
      
      <div class="print-footer">
        <div>Generated on ${new Date().toLocaleString()}</div>
        <div>Staff Management System</div>
      </div>
    `;

    return content;
  };

  // Apply TimePicker styles
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = timePickerStyles;
    document.head.appendChild(styleElement);

    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // Helper function to remove seconds from time strings and format based on user preference
  const removeSeconds = (timeString) => {
    if (!timeString) return '';
    let cleanTime = timeString;
    if (timeString.length === 8 && timeString.indexOf(':') !== timeString.lastIndexOf(':')) {
      cleanTime = timeString.substring(0, 5);
    }

    // Format based on user's time format preference
    return formatTime(cleanTime, timeFormat);
  };

  const isBeforeToday = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  useEffect(() => {
    fetchLocations();
    fetchStaff();
    getSettings();
  }, []);

  // Enhanced Time-off indicator component with navigation
  const TimeOffIndicator = ({ timeOff, onClick }) => {
    const getStatusClass = (status) => {
      switch (status?.toLowerCase()) {
        case 'approved':
          return 'timeoff-approved';
        case 'rejected':
          return 'timeoff-rejected';
        case 'pending':
        default:
          return 'timeoff-requested';
      }
    };

    const getStatusText = (status) => {
      switch (status?.toLowerCase()) {
        case 'approved':
          return t('time_off_approved');
        case 'rejected':
          return t('time_off_rejected');
        case 'pending':
        default:
          return t('time_off_request');
      }
    };

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Navigate to TimeOff page if it's a pending request
      if (timeOff.status?.toLowerCase() === 'pending' || !timeOff.status || timeOff.status?.toLowerCase() === 'requested') {
        navigate('/TimeOff');
      }

      // Call the original onClick if provided
      if (onClick) {
        onClick();
      }
    };

    const isClickable = timeOff.status?.toLowerCase() === 'pending' || !timeOff.status || timeOff.status?.toLowerCase() === 'requested';

    return (
      <div
        className={`timeoff-indicator ${getStatusClass(timeOff.status)} ${isClickable ? 'clickable' : ''}`}
        onClick={handleClick}
        title={`${getStatusText(timeOff.status)}: ${timeOff.reason} - ${timeOff.first_name} ${timeOff.last_name}${isClickable ? ` (${t('click_to_manage')})` : ''}`}
        role={isClickable ? 'button' : 'status'}
        aria-label={`${getStatusText(timeOff.status)} for ${timeOff.first_name} ${timeOff.last_name}`}
      >
        <div className="timeoff-indicator-content">
          <span className="timeoff-status-text">
            {getStatusText(timeOff.status)}
          </span>
          {isClickable && (
            <Icon
              source={ExternalIcon}
              className="external-icon"
            />
          )}
        </div>
      </div>
    );
  };

  const getStaffIdFromTimeOff = (timeOff, staffList) => {
    return staffList.find(staff =>
      staff.first_name === timeOff.first_name &&
      staff.last_name === timeOff.last_name
    )?.id;
  };

  // Update your fetchTimeOffs function
  const fetchTimeOffs = async (startDate, endDate) => {
    try {
      const response = await axiosClient.get('/gettimeoffs');
      if (response.data.success) {
        // Filter time-offs that fall within the date range AND are not declined/rejected
        const filteredTimeOffs = response.data.data.filter(timeOff => {
          const timeOffStartDate = new Date(timeOff.start_date);
          const timeOffEndDate = new Date(timeOff.end_date);
          const rangeStart = new Date(startDate);
          const rangeEnd = new Date(endDate);

          // Exclude declined/rejected time-offs
          const isNotDeclined = !['declined', 'rejected'].includes(timeOff.status?.toLowerCase());

          return (timeOffStartDate <= rangeEnd && timeOffEndDate >= rangeStart) && isNotDeclined;
        });

        setTimeOffs(filteredTimeOffs);
      }
    } catch (err) {
      // Error handled silently
    }
  };

  // Update the getTimeOffForStaffAndDate function
  const getTimeOffForStaffAndDate = (staffId, dateString) => {
    // Helper function to parse date string properly
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    return timeOffs.filter(timeOff => {
      const startDate = parseDate(timeOff.start_date);
      const endDate = parseDate(timeOff.end_date);
      const checkDate = parseDate(dateString);

      // Set times for accurate comparison
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(23, 59, 59, 999);
      checkDate.setHours(0, 0, 0, 0);

      // Get the actual staff ID from time-off data
      const timeOffStaffId = getStaffIdFromTimeOff(timeOff, staffList);

      // Exclude declined/rejected time-offs
      const isNotDeclined = !['declined', 'rejected'].includes(timeOff.status?.toLowerCase());

      const isWithinDateRange = checkDate >= startDate && checkDate <= endDate;

      return timeOffStaffId === staffId && isWithinDateRange && isNotDeclined;
    });
  };

  // Update your useEffect that calls fetchSchedules to also call fetchTimeOffs
  useEffect(() => {
    if (prevSelectedDate === null) {
      setPrevSelectedDate({ start: selectedDate.start, end: selectedDate.end });
      return;
    }

    if (selectedDate.start && selectedDate.end &&
      (prevSelectedDate.start?.getTime() !== selectedDate.start?.getTime() ||
        prevSelectedDate.end?.getTime() !== selectedDate.end?.getTime())) {

      fetchSchedules(selectedDate.start, selectedDate.end);
      fetchTimeOffs(selectedDate.start, selectedDate.end);
      setPrevSelectedDate({ start: selectedDate.start, end: selectedDate.end });
    }
  }, [selectedDate, staffList]);

  // Also update the date change effect for initial load
  useEffect(() => {
    if (selectedView) {
      let firstDay, lastDay;

      if (selectedView === 'weekly') {
        const today = new Date();
        const currentDay = today.getDay();
        const diff = currentDay === 0 ? -6 : 1 - currentDay;

        firstDay = new Date(today);
        firstDay.setDate(today.getDate() + diff);
        firstDay.setHours(0, 0, 0, 0);

        lastDay = new Date(firstDay);
        lastDay.setDate(firstDay.getDate() + 6);
        lastDay.setHours(23, 59, 59, 999);
      } else if (selectedView === 'monthly' || selectedView === 'yearly') {
        firstDay = new Date(year, month, 1);
        firstDay.setHours(0, 0, 0, 0);

        lastDay = new Date(year, month + 1, 0);
        lastDay.setHours(23, 59, 59, 999);
      }

      if (firstDay && lastDay &&
        (!prevSelectedDate ||
          prevSelectedDate.start?.getTime() !== firstDay.getTime() ||
          prevSelectedDate.end?.getTime() !== lastDay.getTime())) {
        setSelectedDate({ start: firstDay, end: lastDay });
      }
    }
  }, [selectedView, month, year]);

  // Make sure fetchTimeOffs is called when staffList is loaded
  useEffect(() => {
    if (staffList.length > 0 && selectedDate.start && selectedDate.end) {
      fetchTimeOffs(selectedDate.start, selectedDate.end);
    }
  }, [staffList]);

  // Auto-assign BLACK color as default when locations are loaded
  useEffect(() => {
    if (locations.length > 0) {
      const storedColors = getLocationColorsFromStorage();
      const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);
      locations.forEach(location => {
        if (!storedColors[location.id.toString()]) {
          // Always assign black color as default for new locations
          saveLocationColorToStorage(location.id, DEFAULT_LOCATION_COLOR.value);
        }
      });

      // Update locations with colors from localStorage (black will be default)
      const updatedLocations = locations.map(location => ({
        ...location,
        color: getLocationColorsFromStorage()[location.id.toString()] || DEFAULT_LOCATION_COLOR.value
      }));
      setLocations(updatedLocations);
    }
  }, [locations.length, t]);

  const fetchSchedules = async (startDate, endDate) => {
    setIsLoading(true);
    try {
      const response = await axiosClient.get('/schedules', {
        params: {
          start_date: formatDateForBackend(startDate),
          end_date: formatDateForBackend(endDate),
          timezone: getUserTimezone()
        }
      });
      const processedSchedules = response.data.schedules.map(schedule => ({
        ...schedule,
        start_time: removeSeconds(schedule.start_time),
        end_time: removeSeconds(schedule.end_time),
        breaks: schedule.breaks ? schedule.breaks.map(breakItem => ({
          ...breakItem,
          start_time: removeSeconds(breakItem.start_time),
          end_time: removeSeconds(breakItem.end_time)
        })) : []
      }));
      setSchedules(processedSchedules);
      setError(null);
    } catch (err) {
      setError(t('error_fetching_schedules'));
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axiosClient.get('/locations');
      if (response.data && response.data.locations) {
        const storedColors = getLocationColorsFromStorage();
        const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);
        const formattedLocations = response.data.locations.map(location => ({
          id: location.id,
          name: location.name,
          color: storedColors[location.id.toString()] || DEFAULT_LOCATION_COLOR.value
        }));
        setLocations(formattedLocations);
      } else {
        setError(t('no_locations_available'));
      }
    } catch (err) {
      setError(t('failed_to_fetch_locations'));
    }
  };

  const fetchStaff = async () => {
    try {
      const response = await axiosClient.get('/getStaffs');
      if (response.data && response.data.staff) {
        const storedColors = getStaffColorsFromStorage();
        const DEFAULT_LOCATION_COLOR = getDefaultLocationColor(t);
        const formattedStaff = response.data.staff.map(member => ({
          ...member,
          color: storedColors[member.id.toString()] || DEFAULT_LOCATION_COLOR.value
        }));
        setStaffList(formattedStaff);
      }
    } catch (err) {
      // Error handled silently
    }
  };

  const getSettings = async () => {
    try {
      const response = await axiosClient.get('/settings_data');
      setSettings(response.data.settings);
      if (response.data.settings && response.data.settings.time_format) {
        setTimeFormat(response.data.settings.time_format);
      }
    } catch (err) {
      // Error handled silently
    }
  };

  // Helper function to update staff color in localStorage
  const updateStaffColor = async (staffId, color) => {
    try {
      saveStaffColorToStorage(staffId, color);

      const updatedStaff = staffList.map(member =>
        member.id.toString() === staffId.toString()
          ? { ...member, color }
          : member
      );
      setStaffList(updatedStaff);

      if (selectedDate.start && selectedDate.end) {
        await fetchSchedules(selectedDate.start, selectedDate.end);
      }
    } catch (err) {
      throw err;
    }
  };

  // Updated function to update location color in localStorage
  const updateLocationColor = async (locationId, color) => {
    try {
      // Save color to localStorage
      saveLocationColorToStorage(locationId, color);

      // Update the locations state to reflect the change
      const updatedLocations = locations.map(location =>
        location.id.toString() === locationId.toString()
          ? { ...location, color }
          : location
      );
      setLocations(updatedLocations);

      // Re-fetch schedules to update display
      if (selectedDate.start && selectedDate.end) {
        await fetchSchedules(selectedDate.start, selectedDate.end);
      }
    } catch (err) {
      throw err;
    }
  };

  const createSchedule = async () => {
    setFormSubmitted(true);
    setValidationErrors({});
    setIsSubmitting(true);

    try {
      if (!staff || !location || !date || !startsAt || !endsAt) {
        setError(t('please_fill_in_all_required_fields'));
        setIsSubmitting(false);
        return;
      }

      const userTimezone = getUserTimezone();

      const parseDate = (dateString) => {
        const [year, month, day] = dateString.split('-');
        return new Date(year, month - 1, day);
      };

      const isBeforeToday = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate < today;
      };

      const selectedDateObj = parseDate(date);
      if (isBeforeToday(selectedDateObj)) {
        setError(t('cannot_create_schedule_for_past_dates'));
        setIsSubmitting(false);
        return;
      }

      let schedules = [];

      const holidayWeekdays = settings?.holiday_weekdays ? JSON.parse(settings.holiday_weekdays) : {
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: true,
        sunday: true
      };

      // Map day numbers to day names
      const dayMap = {
        0: 'sunday',
        1: 'monday',
        2: 'tuesday',
        3: 'wednesday',
        4: 'thursday',
        5: 'friday',
        6: 'saturday'
      };

      // Helper function to check if a date is a holiday
      const isHoliday = (date) => {
        const dayName = dayMap[date.getDay()];
        return holidayWeekdays[dayName];
      };

      // Helper function to create a schedule object
      const createScheduleObject = (date) => {
        // Create a copy of the date for the end date
        let endDateObj = new Date(date);

        // Parse hours to compare
        const startH = parseInt(startsAt.split(':')[0]);
        const endH = parseInt(endsAt.split(':')[0]);

        // If End Hour is less than Start Hour (e.g., 02:00 < 23:00), 
        // it means the shift ends the next day.
        if (endH < startH) {
          endDateObj.setDate(endDateObj.getDate() + 1);
        }

        return {
          staff_id: staff,
          location_id: location,
          start_date: formatDateForBackend(date),
          end_date: formatDateForBackend(endDateObj), // Use the calculated end date
          start_time: startsAt,
          end_time: endsAt,
          timezone: userTimezone,
          notes: notes.trim(),
          breaks: showBreaks ? breaks.map(breakItem => ({
            name: breakItem.name,
            type: breakItem.type || 'paid',
            startsAt: breakItem.startsAt,
            endsAt: breakItem.endsAt
          })) : []
        };
      };
      // For single schedule (no repeat) - ALWAYS ALLOW regardless of holiday status
      if (!repeatShift && !repeatMonth && !repeatYear) {
        schedules.push(createScheduleObject(selectedDateObj));
      }
      // For weekly repeat - Skip holidays
      else if (selectedView === 'weekly' && repeatShift) {
        const selectedDay = selectedDateObj.getDay();
        const diff = selectedDay === 0 ? -6 : 1 - selectedDay;

        const weekStart = new Date(selectedDateObj);
        weekStart.setDate(selectedDateObj.getDate() + diff);
        weekStart.setHours(0, 0, 0, 0);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);
        weekEnd.setHours(23, 59, 59, 999);

        const currentDate = new Date(weekStart);

        while (currentDate <= weekEnd) {
          if (!isBeforeToday(currentDate) && !isHoliday(currentDate)) {
            schedules.push(createScheduleObject(currentDate));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
      // For monthly repeat - Skip holidays
      else if (selectedView === 'monthly' && repeatMonth) {
        const startOfMonth = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), 1);
        const endOfMonth = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth() + 1, 0);
        const currentDate = new Date(startOfMonth);

        while (currentDate <= endOfMonth) {
          if (!isBeforeToday(currentDate) && !isHoliday(currentDate)) {
            schedules.push(createScheduleObject(currentDate));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }
      // For yearly repeat - Skip holidays
      else if (selectedView === 'yearly' && repeatYear) {
        const startDate = new Date(selectedDateObj);
        const endDate = new Date(selectedDateObj);
        endDate.setFullYear(endDate.getFullYear() + 1);
        const currentDate = new Date(startDate);

        while (currentDate < endDate) {
          if (!isBeforeToday(currentDate) && !isHoliday(currentDate)) {
            schedules.push(createScheduleObject(currentDate));
          }
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      if (schedules.length === 0) {
        if (repeatShift || repeatMonth || repeatYear) {
          setError(t('all_dates_in_the_selected_period_are_either_past_dates_or_configured_as_holidays'));
        } else {
          setError(t('unable_to_create_schedule') + '. ' + t('please_check_your_input_data'));
        }
        setIsSubmitting(false);
        return;
      }

      const response = await axiosClient.post('/store_schedules', {
        schedules,
        timezone: userTimezone
      });

      if (response.data.success) {
        handleClose();

        // Force refresh of current view
        if (selectedView === 'weekly') {
          const currentWeekStart = new Date(selectedDate.start);
          const currentWeekEnd = new Date(selectedDate.end);
          await fetchSchedules(currentWeekStart, currentWeekEnd);
          await fetchTimeOffs(currentWeekStart, currentWeekEnd);
        } else if (selectedView === 'monthly') {
          const currentMonthStart = new Date(year, month, 1);
          const currentMonthEnd = new Date(year, month + 1, 0);
          await fetchSchedules(currentMonthStart, currentMonthEnd);
          await fetchTimeOffs(currentMonthStart, currentMonthEnd);
        } else if (selectedView === 'yearly') {
          const currentYearStart = new Date(year, month, 1);
          const currentYearEnd = new Date(year, month + 1, 0);
          await fetchSchedules(currentYearStart, currentYearEnd);
          await fetchTimeOffs(currentYearStart, currentYearEnd);
        }

        const createdCount = response.data.created_count || schedules.length;
        if (createdCount === 1) {
          shopify.toast.show(t('schedule_created'));
        } else {
          shopify.toast.show(`${createdCount} ${t('schedules_created')}`);
        }
      } else {
        setError(response.data.message || t('failed_to_create_schedule'));
      }
    } catch (err) {
      setError(err.response?.data?.message || t('error_creating_schedule'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateSchedule = async () => {
    setFormSubmitted(true);
    setValidationErrors({});
    setIsSubmitting(true);

    try {
      if (!staff || !location || !date || !startsAt || !endsAt) {
        setError(t('please_fill_in_all_required_fields'));
        setIsSubmitting(false);
        return;
      }

      const userTimezone = getUserTimezone();

      const parseDate = (dateString) => {
        const [year, month, day] = dateString.split('-');
        return new Date(year, month - 1, day);
      };

      const isBeforeToday = (date) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const compareDate = new Date(date);
        compareDate.setHours(0, 0, 0, 0);
        return compareDate < today;
      };

      const selectedDateObj = parseDate(date);
      if (isBeforeToday(selectedDateObj)) {
        setError(t('cannot_create_schedule_for_past_dates'));
        setIsSubmitting(false);
        return;
      }

      // Helper function to create a schedule object - same logic as createSchedule
      const createScheduleObject = (date) => {
        // Create a copy of the date for the end date
        let endDateObj = new Date(date);

        // Parse hours to compare
        const startH = parseInt(startsAt.split(':')[0]);
        const endH = parseInt(endsAt.split(':')[0]);

        // If End Hour is less than Start Hour (e.g., 02:00 < 22:00), 
        // it means the shift ends the next day.
        if (endH < startH) {
          endDateObj.setDate(endDateObj.getDate() + 1);
        }

        return {
          staff_id: staff,
          location_id: location,
          start_date: formatDateForBackend(date),
          end_date: formatDateForBackend(endDateObj), // Use the calculated end date
          start_time: startsAt,
          end_time: endsAt,
          timezone: userTimezone,
          notes: notes.trim(),
          breaks: showBreaks ? breaks.map(breakItem => ({
            name: breakItem.name,
            type: breakItem.type || 'paid',
            startsAt: breakItem.startsAt,
            endsAt: breakItem.endsAt
          })) : []
        };
      };

      const scheduleData = createScheduleObject(selectedDateObj);

      const response = await axiosClient.post(`/update_schedules/${editScheduleId}`, scheduleData);
      if (response.data.success) {
        handleClose();

        // Force refresh of current view
        if (selectedView === 'weekly') {
          const currentWeekStart = new Date(selectedDate.start);
          const currentWeekEnd = new Date(selectedDate.end);
          await fetchSchedules(currentWeekStart, currentWeekEnd);
          await fetchTimeOffs(currentWeekStart, currentWeekEnd);
        } else if (selectedView === 'monthly') {
          const currentMonthStart = new Date(year, month, 1);
          const currentMonthEnd = new Date(year, month + 1, 0);
          await fetchSchedules(currentMonthStart, currentMonthEnd);
          await fetchTimeOffs(currentMonthStart, currentMonthEnd);
        } else if (selectedView === 'yearly') {
          const currentYearStart = new Date(year, month, 1);
          const currentYearEnd = new Date(year, month + 1, 0);
          await fetchSchedules(currentYearStart, currentYearEnd);
          await fetchTimeOffs(currentYearStart, currentYearEnd);
        }

        shopify.toast.show(t('shift_updated'));
      } else {
        setError(response.data.message || t('failed_to_update_schedule'));
      }
    } catch (err) {
      if (err.response && err.response.data) {
        if (err.response.data.errors) {
          setValidationErrors(err.response.data.errors);
        }
        if (err.response.data.error) {
          setError(err.response.data.error);
        } else if (err.response.data.message) {
          setError(err.response.data.message);
        } else {
          setError(t('error_updating_schedule'));
        }
      } else {
        setError(t('error_updating_schedule'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const [isEditing, setIsEditing] = useState(false);
  const [editScheduleId, setEditScheduleId] = useState(null);
  const [modalTitle, setModalTitle] = useState(t("create_shift"));

  const handleEditSchedule = async (schedule) => {
    try {
      setError(null);
      setValidationErrors({});

      setIsEditing(true);
      setEditScheduleId(schedule.id);
      setModalTitle(t("edit_shift"));

      setStaff(schedule.staff_id.toString());
      setLocation(schedule.location_id.toString());
      setDate(schedule.start_date);

      // Convert times to 24-hour format for internal storage (TimePicker expects 24-hour format)
      const startTime24 = timeFormat === '12h' ? convert12To24(schedule.start_time) : schedule.start_time;
      const endTime24 = timeFormat === '12h' ? convert12To24(schedule.end_time) : schedule.end_time;

      setStartsAt(startTime24);
      setEndsAt(endTime24);
      setNotes(schedule.notes || '');

      if (schedule.breaks && schedule.breaks.length > 0) {
        const formattedBreaks = schedule.breaks.map(breakItem => ({
          name: breakItem.name || '',
          type: breakItem.type || '',
          // Convert break times to 24-hour format for internal storage
          startsAt: timeFormat === '12h' ? convert12To24(breakItem.start_time || '13:30') : (breakItem.start_time || '13:30'),
          endsAt: timeFormat === '12h' ? convert12To24(breakItem.end_time || '14:30') : (breakItem.end_time || '14:30')
        }));
        setBreaks(formattedBreaks);
        setShowBreaks(true);
      } else {
        setShowBreaks(false);
        setBreaks([{ name: '', type: '', startsAt: '13:30', endsAt: '14:30' }]);
      }

      handleModalChange();
    } catch (error) {
      setError(t('failed_to_load_schedule_details'));
    }
  };

  const calculateTotalHours = (schedules) => {
    if (!schedules || schedules.length === 0) return "00.00";

    let totalMinutes = schedules.reduce((total, schedule) => {
      const start = new Date(`2000/01/01 ${removeSeconds(schedule.start_time)}`);
      const end = new Date(`2000/01/01 ${removeSeconds(schedule.end_time)}`);

      let breakMinutes = 0;
      if (schedule.breaks && schedule.breaks.length > 0) {
        breakMinutes = schedule.breaks.reduce((totalBreak, breakItem) => {
          const breakStart = new Date(`2000/01/01 ${removeSeconds(breakItem.start_time)}`);
          const breakEnd = new Date(`2000/01/01 ${removeSeconds(breakItem.end_time)}`);
          return totalBreak + (breakEnd - breakStart) / (1000 * 60);
        }, 0);
      }

      return total + ((end - start) / (1000 * 60)) - breakMinutes;
    }, 0);

    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    return `${hours.toString().padStart(2, '0')}.${minutes.toString().padStart(2, '0')}`;
  };

  const calculateTotalPay = (schedules, hourlyRate = 20) => {
    if (!schedules || schedules.length === 0) return "0.00";

    const totalMinutes = schedules.reduce((total, schedule) => {
      const start = new Date(`2000/01/01 ${removeSeconds(schedule.start_time)}`);
      const end = new Date(`2000/01/01 ${removeSeconds(schedule.end_time)}`);

      let shiftMinutes = (end - start) / (1000 * 60);

      if (schedule.breaks && schedule.breaks.length > 0) {
        const breakMinutes = schedule.breaks.reduce((totalBreak, breakItem) => {
          if (breakItem.type === 'unpaid') {
            const breakStart = new Date(`2000/01/01 ${removeSeconds(breakItem.start_time)}`);
            const breakEnd = new Date(`2000/01/01 ${removeSeconds(breakItem.end_time)}`);
            return totalBreak + (breakEnd - breakStart) / (1000 * 60);
          }
          return totalBreak;
        }, 0);
        shiftMinutes -= breakMinutes;
      }

      return total + shiftMinutes;
    }, 0);

    const totalHours = totalMinutes / 60;
    return (totalHours * hourlyRate).toFixed(2);
  };

  const generateMonthlyRows = () => {
    const rows = [];
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const daysInMonth = lastDayOfMonth.getDate();

    let startDay = firstDayOfMonth.getDay();
    startDay = startDay === 0 ? 6 : startDay - 1;

    const previousMonthLastDay = new Date(year, month, 0).getDate();
    let currentDay = 1;
    let previousMonthDay = previousMonthLastDay - startDay + 1;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isToday = (day) => {
      const currentDate = new Date();
      return day === currentDate.getDate() &&
        month === currentDate.getMonth() &&
        year === currentDate.getFullYear();
    };

    // Helper function to parse date string properly to avoid timezone issues
    const parseDate = (dateString) => {
      const [year, month, day] = dateString.split('-');
      return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    };

    for (let i = 0; i < 6; i++) {
      const week = [];
      for (let j = 0; j < 7; j++) {
        if (i === 0 && j < startDay) {
          week.push(
            <Box key={i + '-' + j} className="text-center w-full min-h-[100px] border-r border-b border-gray-200 text-gray-400 opacity-50 p-2">
              {previousMonthDay++}
            </Box>
          );
        } else if (currentDay > daysInMonth) {
          week.push(
            <Box key={i + '-' + j} className="text-center w-full min-h-[100px] border-r border-b border-gray-200 text-gray-400 opacity-50 p-2">
              {currentDay++ - daysInMonth}
            </Box>
          );
        } else {
          const currentDate = new Date(year, month, currentDay);
          const isPastDate = isBeforeToday(currentDate);
          const isSunday = j === 6;
          const isCurrentDay = isToday(currentDay);

          const daySchedules = schedules.filter(schedule =>
            schedule.start_date === formatDateForBackend(currentDate)
          );

          // Get time-offs for this date with proper date parsing
          const dayTimeOffs = timeOffs.filter(timeOff => {
            // Parse dates properly to avoid timezone issues
            const timeOffStart = parseDate(timeOff.start_date);
            const timeOffEnd = parseDate(timeOff.end_date);

            // Create current date for comparison
            const currentDateForComparison = new Date(year, month, currentDay);

            // Set all dates to same time for accurate comparison
            timeOffStart.setHours(0, 0, 0, 0);
            timeOffEnd.setHours(23, 59, 59, 999);
            currentDateForComparison.setHours(0, 0, 0, 0);

            // Check if current date falls within the time-off period
            const isWithinTimeOffPeriod = currentDateForComparison >= timeOffStart && currentDateForComparison <= timeOffEnd;

            // Exclude declined/rejected time-offs
            const isNotDeclined = !['declined', 'rejected'].includes(timeOff.status?.toLowerCase());

            return isWithinTimeOffPeriod && isNotDeclined;
          });

          week.push(
            <Box key={i + '-' + j} className={`
                schedule-container relative w-full min-h-[100px] border-r border-b border-gray-200 p-2
                ${isCurrentDay ? 'bg-[#F5F5F5]' : ''}
                ${isPastDate ? 'opacity-50' : ''}
              `}>
              <Box className="flex justify-between items-center mb-2">
                <Text className={`
                    ${isSunday ? 'text-black-500' : ''}
                    ${isCurrentDay ? 'font-bold' : ''}
                  `}>
                  {currentDay}
                </Text>

              </Box>

              <Box className="flex flex-col gap-1">
                {/* Render time-offs first */}
                {dayTimeOffs.map((timeOff, index) => (
                  <TimeOffIndicator
                    key={`timeoff-${timeOff.id || index}`}
                    timeOff={{
                      ...timeOff,
                      staff_name: `${timeOff.first_name} ${timeOff.last_name}`
                    }}
                    onClick={() => {
                      // Time-off indicator clicked
                    }}
                  />
                ))}

                {/* Render ALL schedules with location/staff colors */}
                {daySchedules.map((schedule, index) => {
                  const staff = staffList.find(s => s.id === schedule.staff_id);
                  const location = locations.find(l => l.id.toString() === schedule.location_id.toString());
                  const shiftColor = colorBy === 'staff'
                    ? getStaffColor(schedule.staff_id, staffList)
                    : getLocationColor(schedule.location_id, locations);

                  return (
                    <Box
                      key={`schedule-${schedule.id || index}`}
                      className="rounded p-1 text-[10px] group relative mb-1"
                      style={{
                        backgroundColor: isPastDate ? '#979797' : shiftColor.value,
                        color: isPastDate ? '#FFFFFF' : shiftColor.text,
                        border: `1px solid ${isPastDate ? '#979797' : shiftColor.value}`,
                        boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1)`
                      }}
                    >
                      <Box className="group-hover:opacity-0 transition-opacity duration-200">
                        <Text style={{ color: 'inherit', fontWeight: 'bold' }}>
                          {`${removeSeconds(schedule.start_time)} - ${removeSeconds(schedule.end_time)}`}
                        </Text>
                        <Text style={{ color: 'inherit' }}>
                          {`${staff?.first_name || ''} (${location?.name || ''})`}
                        </Text>
                        {schedule.notes && (
                          <Text style={{ color: 'inherit', fontSize: '8px', fontStyle: 'italic', marginTop: '2px' }}>
                            📝 {schedule.notes.split(' ').slice(0, 3).join(' ')}
                            {schedule.notes.split(' ').length > 3 ? '...' : ''}
                          </Text>
                        )}
                      </Box>
                      {!isPastDate && (
                        <Box className="absolute inset-0 flex justify-center items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/20 rounded">
                          <Box className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditSchedule(schedule);
                              }}
                              className="p-1 bg-white rounded hover:bg-gray-100"
                            >
                              <Icon source={EditIcon} tone="base" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSchedule(schedule.id);
                              }}
                              className="p-1 bg-white rounded hover:bg-red-50"
                            >
                              <Icon source={DeleteIcon} tone="critical" />
                            </button>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  );
                })}

                {/* Add button for creating new shifts - always show unless past date */}
                {!isPastDate && (
                  <Box
                    onClick={(e) => {
                      e.stopPropagation();
                      setError(null);
                      setValidationErrors({});
                      setStaff('');
                      setLocation('');
                      setDate(formatDateForBackend(currentDate));
                      setStartsAt('09:00');
                      setEndsAt('17:00');
                      setNotes('');
                      setIsEditing(false);
                      setModalTitle(t("create_shift"));
                      setShowBreaks(false);
                      handleModalChange();
                    }}
                    className={`
                      rounded text-center cursor-pointer transition-all duration-200 flex items-center justify-center
                      ${daySchedules.length > 0
                        ? 'mt-1 py-1 bg-gray-100 hover:bg-gray-200 border border-dashed border-gray-300 opacity-50 hover:opacity-100'
                        : 'absolute inset-0 opacity-0 hover:opacity-100 bg-gray-50'
                      } 
                    `}
                    title={t("add_shift")}
                  >
                    <Icon source={PlusCircleIcon} tone="base" />
                  </Box>
                )}

                {/* Show "No Shift" placeholder if empty and past */}
                {daySchedules.length === 0 && isPastDate && (
                  <Box className="h-full flex items-center justify-center bg-gray-50 rounded">
                    <Icon source={DisabledIcon} tone="subdued" />
                  </Box>
                )}
              </Box>

              {isCurrentDay && (
                <Box className="absolute top-1 right-1 w-2 h-2 bg-[#00848e] rounded-full" />
              )}
            </Box>
          );
          currentDay++;
        }
      }
      rows.push(week);
    }
    return rows;
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await axiosClient.post(`/destroy_schedules/${scheduleId}`);
      if (selectedView === 'weekly') {
        const firstDay = new Date(selectedDate.start);
        const lastDay = new Date(selectedDate.end);
        fetchSchedules(firstDay, lastDay);
        fetchTimeOffs(firstDay, lastDay);
      } else {
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        fetchSchedules(firstDay, lastDay);
        fetchTimeOffs(firstDay, lastDay);
      }
      shopify.toast.show(t('schedule_deleted'));
    } catch (error) {
      shopify.toast.show(t('failed_to_delete_schedule'), { error: true });
    }
  };

  const handleModalChange = useCallback(() => {
    if (!active) {
      setError(null);
      setValidationErrors({});
    }
    setActive(!active);
  }, [active, isEditing]);

  const rows = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isBeforeToday = (date) => {
      const compareDate = new Date(date);
      compareDate.setHours(0, 0, 0, 0);
      return compareDate < today;
    };

    if (selectedView === 'monthly' || selectedView === 'yearly') {
      return generateMonthlyRows();
    }

    return staffList.map(staffMember => {
      const staffSchedules = schedules.filter(schedule =>
        schedule.staff_id === staffMember.id
      );

      const weekDays = Array(7).fill(null).map((_, index) => {
        const dayDate = new Date(selectedDate.start);
        dayDate.setDate(dayDate.getDate() + index);
        const isPastDate = isBeforeToday(dayDate);

        const daySchedules = staffSchedules.filter(schedule =>
          schedule.start_date === formatDateForBackend(dayDate)
        );

        // Get time-offs for this staff and date - already filtered to exclude declined
        const staffTimeOffs = getTimeOffForStaffAndDate(staffMember.id, formatDateForBackend(dayDate));

        // Availability badge: check staffMember.availability for this weekday
        const dayName = dayDate.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        const availabilityData = parseAvailability(staffMember.days_availability);
        const isUnavailable = availabilityData[dayName]?.available === false;

        return (
          <Box className="schedule-container relative h-full min-h-[100px] flex flex-col gap-1 p-1">
            {/* Unavailability badge — click to edit */}
            {isUnavailable && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openAvailabilityModal(staffMember); }}
                title="Click to edit availability"
                style={{
                  position: 'relative',
                  zIndex: 10,
                  fontSize: '10px',
                  fontWeight: '600',
                  color: '#B91C1C',
                  backgroundColor: '#FEE2E2',
                  border: '1px solid #FECACA',
                  borderRadius: '4px',
                  padding: '2px 6px',
                  textAlign: 'center',
                  marginBottom: '2px',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                Unavailable ✎
              </button>
            )}
            {/* Render time-off indicators first - only non-declined ones will show */}
            {staffTimeOffs.length > 0 && (
              <Box className="flex flex-col gap-1 mb-1">
                {staffTimeOffs.map((timeOff, timeOffIndex) => {
                  return (
                    <TimeOffIndicator
                      key={`timeoff-${timeOffIndex}`}
                      timeOff={{
                        ...timeOff,
                        staff_name: `${timeOff.first_name} ${timeOff.last_name}`
                      }}
                      onClick={() => {
                        // Time-off indicator clicked
                      }}
                    />
                  );
                })}
              </Box>
            )}

            {/* 2. RENDER ALL SCHEDULES (Map instead of single check) */}
            {daySchedules.map((daySchedule, idx) => {
              const location = locations.find(l => l.id.toString() === daySchedule.location_id.toString());
              const shiftColor = colorBy === 'staff'
                ? getStaffColor(daySchedule.staff_id, staffList)
                : getLocationColor(daySchedule.location_id, locations);

              return (
                <Box
                  key={`shift-${daySchedule.id}`}
                  className={`${isPastDate ? 'opacity-50' : ''} rounded text-[10px] text-center p-1 mb-1 ${isPastDate ? '' : 'group relative'}`}
                  style={{
                    backgroundColor: isPastDate ? '#979797' : shiftColor.value,
                    color: isPastDate ? '#FFFFFF' : shiftColor.text,
                    border: `1px solid ${isPastDate ? '#979797' : shiftColor.value}`
                  }}
                >
                  <Box>
                    <Text style={{ color: 'inherit', fontWeight: 'bold' }}>
                      {`${removeSeconds(daySchedule.start_time)} - ${removeSeconds(daySchedule.end_time)}`}
                    </Text>
                    <Text style={{ color: 'inherit' }}>
                      {location?.name || ''}
                    </Text>
                  </Box>

                  {/* Edit/Delete Buttons (Show on Hover) */}
                  {!isPastDate && (
                    <Box className="absolute inset-0 flex justify-center items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-black/20 rounded">
                      <div className="flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEditSchedule(daySchedule); }}
                          className="p-1 bg-white rounded hover:bg-gray-100"
                        >
                          <Icon source={EditIcon} tone="base" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteSchedule(daySchedule.id); }}
                          className="p-1 bg-white rounded hover:bg-red-50"
                        >
                          <Icon source={DeleteIcon} tone="critical" />
                        </button>
                      </div>
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* 3. ALWAYS SHOW ADD BUTTON (Unless past date) */}
            {!isPastDate && (
              <Box
                onClick={(e) => {
                  // Stop propagation to prevent triggering parent clicks if any
                  e.stopPropagation();

                  setStaff(staffMember.id.toString());
                  setDate(formatDateForBackend(dayDate));
                  // Default to a time likely to be a second shift (e.g., Evening)
                  setStartsAt('17:00');
                  setEndsAt('21:00');
                  setIsEditing(false);
                  setModalTitle(t("create_shift"));
                  setShowBreaks(false);
                  handleModalChange();
                }}
                className={`
                        rounded text-center cursor-pointer transition-all duration-200 flex items-center justify-center
                        ${daySchedules.length > 0
                    ? 'mt-1 py-1 bg-gray-100 hover:bg-gray-200 border border-dashed border-gray-300 opacity-50 hover:opacity-100' // Style when shifts exist (Stack below)
                    : 'absolute inset-0 opacity-0 hover:opacity-100 bg-gray-50' // Style when empty (Fill cell)
                  } 
                    `}
                // Add a tooltip or title for better UX
                title={t("add_shift")}
              >
                <Icon source={PlusCircleIcon} tone="base" />
              </Box>
            )}

            {/* Show "No Shift" placeholder if empty and past */}
            {daySchedules.length === 0 && isPastDate && (
              <Box className="h-full flex items-center justify-center bg-gray-50 rounded">
                <Icon source={DisabledIcon} tone="subdued" />
              </Box>
            )}
          </Box>
        );
      });

      return [
        <Box className="text-center w-fit">
          <Text variant='headingSm'>{staffMember.first_name} {staffMember.last_name}</Text>
        </Box>,
        ...weekDays
      ];
    });
  }, [selectedView, schedules, timeOffs, staffList, selectedDate, locations, handleModalChange, colorBy]);

  const handleDeleteBreak = (index) => {
    const updatedBreaks = breaks.filter((_, i) => i !== index);
    setBreaks(updatedBreaks);

    if (updatedBreaks.length === 0) {
      setShowBreaks(false);
    }
  };

  const handleBreakChange = (index, field, value) => {
    setBreaks(breaks.map((breakItem, i) =>
      i === index ? { ...breakItem, [field]: value } : breakItem
    ));
  };

  const handleSelectChange = useCallback((value) => setSelectedView(value), []);

  const renderTotals = (date, schedules) => {
    const dateSchedules = schedules.filter(schedule =>
      new Date(schedule.start_date).toDateString() === date.toDateString()
    );

    const totalShifts = dateSchedules.length;
    const totalHours = calculateTotalHours(dateSchedules);
    const totalPay = calculateTotalPay(dateSchedules, 20);

    return (
      <Box className="text-center w-fit text-[9px]">
        <div className="flex w-fit gap-1">
          <Icon source={ClockIcon} tone="base" />
          <Text>{totalHours}</Text>
          <Icon source={CashDollarIcon} tone="base" />
          <Text>${totalPay}</Text>
        </div>
        <Box className="flex gap-1 items-center w-fit">
          <Icon source={PersonFilledIcon} tone="base" />
          <Text variant='headingSm'>{totalShifts}</Text>
        </Box>
      </Box>
    );
  };

  const headings = useMemo(() => {
    if (selectedView === 'monthly' || selectedView === 'yearly') {
      return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    }

    const weeklyHeadings = [];
    const startDate = new Date(selectedDate.start);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      const isPastDate = isBeforeToday(date);
      const isCurrentDay = date.toDateString() === today.toDateString();

      const dayOfWeek = date.toLocaleDateString('en-US', { weekday: 'short' });
      const dayOfMonth = date.getDate();

      weeklyHeadings.push(
        <Box className={`
            text-center relative
            ${isPastDate ? 'opacity-50' : ''}
            ${isCurrentDay ? 'bg-[#E3E3E3] rounded-md p-1' : ''}
          `}>
          <Text tone={isPastDate ? 'subdued' : 'default'}>
            {`${dayOfWeek} ${dayOfMonth}`}
          </Text>
          {isPastDate && (
            <Box className="flex items-center justify-center gap-1">
              <Icon source="lock" tone="subdued" className="w-3 h-3" />
              <Text variant="bodySmall" tone="subdued" className="text-[8px]">
                {t('past')}
              </Text>
            </Box>
          )}
          {isCurrentDay && (
            <Box className="absolute top-0 right-0 w-2 h-2 bg-[#00848e] rounded-full m-1" />
          )}
        </Box>
      );
    }

    return ['Name', ...weeklyHeadings];
  }, [selectedView, selectedDate]);

  const totals = useMemo(() => {
    if (selectedView === 'monthly' || selectedView === 'yearly') return [];
    return ['Total', ...Array(7).fill(null).map((_, index) => {
      const date = new Date(selectedDate.start);
      date.setDate(date.getDate() + index);
      return renderTotals(date, schedules);
    })];
  }, [selectedView, selectedDate, schedules]);

  const options = [
    { label: t('weekly'), value: 'weekly' },
    { label: t('monthly'), value: 'monthly' },
    { label: t('yearly'), value: 'yearly' },
  ];

  const yearOptions = Array.from({ length: 10 }, (_, i) => ({
    label: `${currentYear + i}`,
    value: currentYear + i,
  }));

  const monthOptions = [
    { label: t('january'), value: 0 },
    { label: t('february'), value: 1 },
    { label: t('march'), value: 2 },
    { label: t('april'), value: 3 },
    { label: t('may'), value: 4 },
    { label: t('june'), value: 5 },
    { label: t('july'), value: 6 },
    { label: t('august'), value: 7 },
    { label: t('september'), value: 8 },
    { label: t('october'), value: 9 },
    { label: t('november'), value: 10 },
    { label: t('december'), value: 11 },
  ];

  const handleMonthSelect = useCallback((value) => {
    const numValue = parseInt(value);
    setSelectedMonth(numValue);
    setDateState(prev => ({ ...prev, month: numValue }));
  }, []);

  const handleYearSelect = useCallback((value) => {
    const numValue = parseInt(value);
    setSelectedYear(numValue);
    setDateState(prev => ({ ...prev, year: numValue }));
  }, []);

  const breakTypeOptions = [
    { label: t('paid'), value: 'paid' },
    { label: t('unpaid'), value: 'unpaid' },
  ];

  const staffOptions = useMemo(() =>
    staffList.map(staff => ({
      label: `${staff.first_name} ${staff.last_name}`,
      value: staff.id.toString()
    })), [staffList]
  );

  const locationOptions = useMemo(() => {
    if (!locations || !Array.isArray(locations)) return [];
    return locations.map(location => {
      const locationColor = getLocationColor(location.id, locations);
      return {
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div
              className="location-color-preview"
              style={{ backgroundColor: locationColor.value }}
            />
            {location.name}
          </div>
        ),
        value: location.id.toString()
      };
    });
  }, [locations]);

  const handleAddBreak = () => {
    if (!showBreaks) {
      setShowBreaks(true);
      setBreaks([{ name: '', type: '', startsAt: '13:30', endsAt: '14:30' }]);
    } else {
      setBreaks([...breaks, { name: '', type: '', startsAt: '13:30', endsAt: '14:30' }]);
    }
  };

  const handleClose = () => {
    setActive(false);
    setBreaks([{ name: '', type: '', startsAt: '', endsAt: '' }]);
    setLocation('');
    setStaff('');
    setDate('');
    setStartsAt('');
    setEndsAt('');
    setNotes('');
    setShowBreaks(false);
    setIsEditing(false);
    setEditScheduleId(null);
    setModalTitle(t("create_shift"));
    setRepeatShift(false);
    setRepeatMonth(false);
    setRepeatYear(false);
    setFormSubmitted(false);
    setValidationErrors({});
    setIsSubmitting(false);
  };

  return (
    <Page fullWidth title={t('schedule')}>
      {isLoading && (
        <div className="flex justify-center p-4">
          <Spinner accessibilityLabel={t('loading_schedules')} size="large" />
        </div>
      )}

      <Box className="flex items-center justify-between mb-4">
        <Box className="flex items-center gap-2">
          <Button
            icon={ColorIcon}
            onClick={() => setColorModalActive(true)}
            variant="secondary"
          >
            {t('customize_colors')}
          </Button>
          <Popover
            active={colorByPopoverActive}
            activator={
              <Button
                onClick={toggleColorByPopoverActive}
                disclosure
                variant="secondary"
              >
                {colorBy === 'location'
                  ? (t('color_by_location') || 'Color by Location')
                  : (t('color_by_staff') || 'Color by Staff')}
              </Button>
            }
            onClose={toggleColorByPopoverActive}
          >
            <ActionList
              actionRole="menuitem"
              items={[
                {
                  content: t('color_by_location') || 'Color by Location',
                  active: colorBy === 'location',
                  onAction: () => {
                    handleColorByChange('location');
                    toggleColorByPopoverActive();
                  }
                },
                {
                  content: t('color_by_staff') || 'Color by Staff',
                  active: colorBy === 'staff',
                  onAction: () => {
                    handleColorByChange('staff');
                    toggleColorByPopoverActive();
                  }
                }
              ]}
            />
          </Popover>
          {selectedView === 'weekly' && (
            <Button
              icon={DuplicateIcon}
              onClick={() => setCopyModalActive(true)}
              variant="secondary"
            >
              {t('copy_week')}
            </Button>
          )}
          <Button
            icon={PrintIcon}
            onClick={handlePrint}
            variant="secondary"
          >
            {t('print_schedule')}
          </Button>
          <Box
            position="relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {(showClearShiftsTooltip || isHovered) && (
              <div style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '10px',
                backgroundColor: 'white',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                width: '260px',
                zIndex: 100,
                border: '1px solid #e1e3e5'
              }}>
                <div style={{ position: 'relative' }}>
                  <Text variant="bodySm" as="p">
                    {t('clear_all_shifts_tooltip')}
                  </Text>
                  {showClearShiftsTooltip && (
                    <div
                      onClick={handleDismissTooltip}
                      style={{
                        position: 'absolute',
                        top: '-8px',
                        right: '-8px',
                        cursor: 'pointer',
                        padding: '4px'
                      }}
                    >
                      <Icon source={XSmallIcon} tone="subdued" />
                    </div>
                  )}
                </div>
                {/* Arrow */}
                <div style={{
                  position: 'absolute',
                  bottom: '-6px',
                  left: '50%',
                  transform: 'translateX(-50%) rotate(45deg)',
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  borderBottom: '1px solid #e1e3e5',
                  borderRight: '1px solid #e1e3e5'
                }} />
              </div>
            )}
            <Button
              icon={DeleteIcon}
              onClick={() => setDeleteShiftsModalActive(true)}
              variant="secondary"
              tone="critical"
            >
              {t('clear_all_shifts') || 'Clear All Shifts'}
            </Button>
          </Box>
        </Box>

        <Box className="flex items-center justify-end gap-4">
          {selectedView !== 'yearly' ? (
            <DatePickerExample
              selectedView={selectedView}
              setSelectedDate={setSelectedDate}
              setDateState={setDateState}
            />
          ) : (
            <>
              <Select
                label={t('month')}
                options={monthOptions}
                onChange={handleMonthSelect}
                value={selectedMonth}
                labelHidden
              />
              <Select
                label={t('year')}
                options={yearOptions}
                onChange={handleYearSelect}
                value={selectedYear}
                labelHidden
              />
            </>
          )}
          <Select
            options={options}
            onChange={handleSelectChange}
            value={selectedView}
          />
        </Box>
      </Box>

      <Card padding={200}>
        {!isLoading && schedules.length === 0 && staffList.length > 0 ? (
          <Box padding="800">
            <BlockStack align="center" inlineAlign="center" gap="400">
              <Box
                background="bg-surface-secondary"
                borderRadius="full"
                padding="400"
              >
                <Icon source={CalendarIcon} tone="subdued" />
              </Box>
              <BlockStack align="center" inlineAlign="center" gap="200">
                <Text variant="headingMd" as="h3">
                  {t("no_shifts_scheduled")}
                </Text>
                <Text tone="subdued" alignment="center">
                  {t("create_first_shift_description")}
                </Text>
              </BlockStack>
              <Button variant="primary" onClick={() => setActive(true)}>
                {t("create_shift")}
              </Button>
            </BlockStack>
          </Box>
        ) : selectedView === 'monthly' || selectedView === 'yearly' ? (
          <div className="grid grid-cols-7 gap-0">
            {[t('mon'), t('tue'), t('wed'), t('thu'), t('fri'), t('sat'), t('sun')].map(day => (
              <Box key={day} className="text-center p-2 font-medium border-b border-r border-gray-200">
                {day}
              </Box>
            ))}
            {rows.map((week, i) => (
              <React.Fragment key={i}>
                {week}
              </React.Fragment>
            ))}
          </div>
        ) : (
          <div id="DataTable">
            <DataTable
              columnContentTypes={['text']}
              headings={headings}
              rows={rows}
              totals={totals}
              totalsName={{
                singular: t('total'),
                plural: t('totals'),
              }}
              showTotalsInFooter
              style={{ borderCollapse: 'collapse' }}
            />
          </div>
        )}
      </Card>

      {/* Color Settings Modal (unified) */}
      <ColorSettingsModal
        active={colorModalActive}
        onClose={() => setColorModalActive(false)}
        locations={locations}
        staffList={staffList}
        onUpdateLocationColor={updateLocationColor}
        onUpdateStaffColor={updateStaffColor}
      />

      <CopyWeekModal
        active={copyModalActive}
        onClose={() => setCopyModalActive(false)}
        currentStartDate={selectedDate.start}
        onCopyWeek={handleCopyWeek}
      />

      <DeleteAllShiftsModal
        active={deleteShiftsModalActive}
        onClose={() => setDeleteShiftsModalActive(false)}
        staffList={staffList}
        onDelete={handleBulkDeleteShifts}
      />

      {/* Availability edit modal */}
      <Modal
        open={availabilityModal.open}
        onClose={() => setAvailabilityModal(prev => ({ ...prev, open: false }))}
        title={`Edit Availability — ${availabilityModal.staffName}`}
        primaryAction={{
          content: 'Save',
          onAction: handleSaveAvailability,
          loading: isSavingAvailability,
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setAvailabilityModal(prev => ({ ...prev, open: false })),
        }]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            <Text variant="bodyMd" color="subdued">
              Click a day to toggle availability. Green = available.
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {DAY_KEYS.map(day => {
                const isAvailable = availabilityModal.availability[day]?.available !== false;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => handleAvailabilityToggle(day)}
                    style={{
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `2px solid ${isAvailable ? '#008060' : '#C9CCCF'}`,
                      backgroundColor: isAvailable ? '#008060' : '#F6F6F7',
                      color: isAvailable ? '#FFFFFF' : '#6D7175',
                      fontWeight: isAvailable ? '600' : '400',
                      fontSize: '13px',
                      cursor: 'pointer',
                      textTransform: 'capitalize',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {day.charAt(0).toUpperCase() + day.slice(1)}
                  </button>
                );
              })}
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>

      <Modal
        open={active}
        onClose={handleClose}
        title={modalTitle}
        primaryAction={{
          content: isEditing ? t('update_shift') : t('create_shift'),
          onAction: isEditing ? updateSchedule : createSchedule,
          loading: isSubmitting,
          disabled: !staff || !location || !date || !startsAt || !endsAt || isSubmitting
        }}
        secondaryActions={[
          {
            content: t('cancel'),
            onAction: handleClose,
          },
        ]}
      >
        <Modal.Section>
          {error && (
            <Banner tone="critical">
              {error}
            </Banner>
          )}

          <Box padding="4">
            <Select
              label={t('staff')}
              options={staffOptions}
              onChange={(value) => setStaff(value)}
              value={staff}
              fullWidth
              error={formSubmitted && !staff && t('staff_is_required')}
              placeholder={t('select_a_staff')}
            />
            {validationErrors.staff_id && (
              <Text variant="bodyMd" color="critical">
                {validationErrors.staff_id}
              </Text>
            )}
          </Box>

          <Box padding="4">
            <Select
              label={t('location')}
              options={locationOptions}
              onChange={value => setLocation(value)}
              value={location}
              fullWidth
              error={formSubmitted && !location && t('location_is_required')}
              disabled={locationOptions.length === 0}
              placeholder={t('select_a_location')}
            />
            {validationErrors.location_id && (
              <Text variant="bodyMd" color="critical">
                {validationErrors.location_id}
              </Text>
            )}
            {locationOptions.length === 0 && (
              <Text variant="bodyMd" color="critical">
                {t('no_locations_available')}
              </Text>
            )}
          </Box>

          <Box padding="4">
            <TextField
              label={t('date')}
              type="date"
              value={date}
              onChange={setDate}
              error={formSubmitted && !date && t('date_is_required')}
            />
            {validationErrors.start_date && (
              <Text variant="bodyMd" color="critical">
                {validationErrors.start_date}
              </Text>
            )}
          </Box>

          <Box padding="4">
            <Box className="grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Box>
                <Text variant="bodyMd" as="label" color="subdued">{t('start_at')}</Text>
                <TimePicker
                  onChange={setStartsAt}
                  value={startsAt}
                  timeFormat={timeFormat}
                />
              </Box>
              <Box>
                <Text variant="bodyMd" as="label" color="subdued">{t('end_at')}</Text>
                <TimePicker
                  onChange={setEndsAt}
                  value={endsAt}
                  timeFormat={timeFormat}
                />
              </Box>
            </Box>
          </Box>

          <Box padding="4">
            <TextField
              label={t('luck_note')}
              value={notes}
              onChange={setNotes}
              multiline={3}
              placeholder={t('add_notes_for_this_shift')}
              helpText={t('these_notes_will_be_visible_to_staff_when_they_clock_in')}
            />
          </Box>

          {!showBreaks ? (
            <Box padding="4">
              <Button variant="plain" onClick={handleAddBreak}>{t('add_break')}</Button>
            </Box>
          ) : (
            <Box padding="4">
              {breaks.map((breakItem, index) => (
                <Box key={index} className="grid grid-cols-[2fr_1fr_1fr_20px] gap-4 items-end py-1">
                  <TextField
                    label={t('name')}
                    value={breakItem.name}
                    onChange={(value) => handleBreakChange(index, 'name', value)}
                    error={validationErrors[`breaks.${index}.name`] ? true : undefined}
                  />
                  <Box>
                    <Text variant="bodyMd" as="label">{t('starts_at')}</Text>
                    <TimePicker onChange={(value) => handleBreakChange(index, 'startsAt', value)} value={breakItem.startsAt} timeFormat={timeFormat} />
                  </Box>
                  <Box>
                    <Text variant="bodyMd" as="label">{t('ends_at')}</Text>
                    <TimePicker onChange={(value) => handleBreakChange(index, 'endsAt', value)} value={breakItem.endsAt} timeFormat={timeFormat} />
                  </Box>
                  <Box className="mb-1">
                    <Button variant="plain" destructive onClick={() => handleDeleteBreak(index)}>
                      <Icon source={DeleteIcon} color="base" tone="critical" />
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}

          {!isEditing && (
            <Box padding="4">
              <BlockStack>
                {selectedView === 'weekly' ? (
                  <Checkbox
                    label={t('repeat_this_shift_for_the_rest_of_the_week')}
                    checked={repeatShift}
                    onChange={setRepeatShift}
                  />
                ) : selectedView === 'monthly' ? (
                  <Checkbox
                    label={t('repeat_this_shift_for_the_entire_month')}
                    checked={repeatMonth}
                    onChange={setRepeatMonth}
                  />
                ) : (
                  <Checkbox
                    label={t('repeat_this_shift_for_the_entire_year')}
                    checked={repeatYear}
                    onChange={setRepeatYear}
                  />
                )}
              </BlockStack>
            </Box>
          )}
        </Modal.Section>
      </Modal>

    </Page>
  );
}

function DatePickerExample({ selectedView, setSelectedDate, setDateState }) {
  const [visible, setVisible] = useState(false);
  const [selectedDate, setSelectedDateState] = useState({ start: new Date(), end: new Date() });
  const [{ month, year }, setDate] = useState({
    month: selectedDate.start.getMonth(),
    year: selectedDate.start.getFullYear(),
  });

  const formattedValue = `${formatDateForBackend(selectedDate.start)} to ${formatDateForBackend(selectedDate.end)}`;
  const datePickerRef = useRef(null);

  const nodeContainsDescendant = (rootNode, descendant) => {
    if (!rootNode || !descendant) return false;
    if (rootNode === descendant) return true;
    let parent = descendant.parentNode;
    while (parent != null) {
      if (parent === rootNode) return true;
      parent = parent.parentNode;
    }
    return false;
  };

  const isNodeWithinPopover = (node) => datePickerRef?.current ? nodeContainsDescendant(datePickerRef.current, node) : false;

  const handleInputValueChange = () => {};

  const handleOnClose = ({ relatedTarget }) => {
    if (!isNodeWithinPopover(relatedTarget)) setVisible(false);
  };

  const handleMonthChange = (month, year) => {
    setDate({ month, year });
    setDateState({ month, year });

    // Update date range when month changes in monthly view
    if (selectedView === 'monthly') {
      const firstDay = new Date(year, month, 1);
      firstDay.setHours(0, 0, 0, 0);
      const lastDay = new Date(year, month + 1, 0);
      lastDay.setHours(23, 59, 59, 999);

      const newDateRange = { start: firstDay, end: lastDay };
      setSelectedDateState(newDateRange);
      setSelectedDate(newDateRange);
    }
  };

  const handleDateSelection = ({ start }) => {
    if (selectedView === 'weekly') {
      const selectedDate = new Date(start);
      const day = selectedDate.getDay();
      const diff = day === 0 ? -6 : 1 - day;

      const firstDay = new Date(selectedDate);
      firstDay.setDate(selectedDate.getDate() + diff);

      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);

      const newDateRange = { start: firstDay, end: lastDay };
      setSelectedDateState(newDateRange);
      setSelectedDate(newDateRange);
    } else if (selectedView === 'monthly') {
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);

      const newDateRange = { start: firstDay, end: lastDay };
      setSelectedDateState(newDateRange);
      setSelectedDate(newDateRange);
    }
  };

  useEffect(() => {
    const today = new Date();

    if (selectedView === 'weekly') {
      const currentDay = today.getDay();
      const diff = currentDay === 0 ? -6 : 1 - currentDay;

      const firstDay = new Date(today);
      firstDay.setDate(today.getDate() + diff);
      firstDay.setHours(0, 0, 0, 0);

      const lastDay = new Date(firstDay);
      lastDay.setDate(firstDay.getDate() + 6);
      lastDay.setHours(23, 59, 59, 999);

      const newDateRange = { start: firstDay, end: lastDay };
      setSelectedDateState(newDateRange);
      setSelectedDate(newDateRange);
    } else if (selectedView === 'monthly') {
      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();

      const firstDay = new Date(currentYear, currentMonth, 1);
      firstDay.setHours(0, 0, 0, 0);
      const lastDay = new Date(currentYear, currentMonth + 1, 0);
      lastDay.setHours(23, 59, 59, 999);

      const newDateRange = { start: firstDay, end: lastDay };
      setSelectedDateState(newDateRange);
      setSelectedDate(newDateRange);
    }

    setDate({ month: today.getMonth(), year: today.getFullYear() });
  }, [selectedView]);

  return (
    <BlockStack inlineAlign="center" gap="400">
      <Box minWidth="276px" padding={{ xs: 200 }}>
        <Popover
          active={visible}
          autofocusTarget="none"
          preferredAlignment="left"
          fullWidth
          preferInputActivator={false}
          preferredPosition="below"
          preventCloseOnChildOverlayClick
          onClose={handleOnClose}
          activator={
            <TextField
              role="combobox"
              prefix={<Icon source={CalendarIcon} />}
              value={formattedValue}
              onFocus={() => setVisible(true)}
              onChange={handleInputValueChange}
              autoComplete="off"
            />
          }
        >
          <Card>
            <div ref={datePickerRef}>
              <DatePicker
                month={month}
                year={year}
                selected={selectedDate}
                onMonthChange={handleMonthChange}
                onChange={handleDateSelection}
                allowRange
              />
            </div>
          </Card>
        </Popover>
      </Box>
    </BlockStack>
  );
}
